import React, { useEffect, useLayoutEffect, useState, useRef, useCallback } from 'react';
import {
  syncNewsGoogleFontLinkElements,
  syncNewsCustomCssLinks,
  collectGoogleFontNamesFromStacks,
} from '../utils/googleFontsWelcome';
import {
  getNewsContentBackgroundCss,
  getNewsCardBackgroundCss,
  NEWS_POLL_CARD_BACKGROUND_PRESETS,
} from '../utils/newsContentBackgrounds';
import { extractFirstCssTextColorFromHtml } from '../utils/newsContentColorMeta';
import { isNewsHtmlBodyEmpty } from '../utils/newsHtmlBodyEmpty';
import styled from 'styled-components';
import CustomEmojiPicker from './Common/EmojiPicker';
import { FaUserCircle, FaSort } from 'react-icons/fa';
import DOMPurify from 'dompurify';
import { getCustomEmojiSizeForArea, subscribeEmojiSettings } from '../utils/emojiSettings';

// Декодируем HTML-сущности, сохраняя теги (не превращаем HTML в простой текст)
const decodeHtmlEntities = (html) => {
  if (!html) return '';
  try {
    const container = document.createElement('div');
    // Помещаем строку в DOM, чтобы браузер раскодировал сущности
    container.innerHTML = html;
    // Возвращаем HTML как строку с сохранением тегов
    return container.innerHTML;
  } catch (e) {
    return html;
  }
};

/** Убираем inline font-family у контента новости, чтобы применялся шрифт с карточки (Google Fonts). pre/code не трогаем. */
function stripInlineFontFamilyExceptPre(html) {
  if (!html || typeof document === 'undefined') return html;
  try {
    const d = document.createElement('div');
    d.innerHTML = html;
    d.querySelectorAll('[style]').forEach((el) => {
      if (el.closest && el.closest('pre')) return;
      const s = el.getAttribute('style');
      if (!s) return;
      const next = s
        .replace(/\bfont-family\s*:\s*[^;]+;?/gi, '')
        .replace(/;\s*;/g, ';')
        .trim()
        .replace(/^;+|;+$/g, '')
        .trim();
      if (next) el.setAttribute('style', next);
      else el.removeAttribute('style');
    });
    return d.innerHTML;
  } catch {
    return html;
  }
}

const safeHtml = (html) =>
  stripInlineFontFamilyExceptPre(DOMPurify.sanitize(decodeHtmlEntities(html)));

const stripHtml = (html) => decodeHtmlEntities(html).replace(/\s+/g, ' ').trim();

function pickCardBackgroundMedia({
  cardImageDesktopRaw,
  cardImageMobileRaw,
  cardVideoDesktopRaw,
  cardVideoMobileRaw,
  preferMobile,
}) {
  const order = preferMobile
    ? [
        ['video', cardVideoMobileRaw],
        ['image', cardImageMobileRaw],
        ['video', cardVideoDesktopRaw],
        ['image', cardImageDesktopRaw],
      ]
    : [
        ['video', cardVideoDesktopRaw],
        ['image', cardImageDesktopRaw],
        ['video', cardVideoMobileRaw],
        ['image', cardImageMobileRaw],
      ];
  for (const [type, raw] of order) {
    const u = raw && String(raw).trim();
    if (u) return { type, raw: u };
  }
  return null;
}

function newsItemHasUserFacingText(item) {
  if (!item) return false;
  if (stripHtml(item.title || '').length > 0) return true;
  return !isNewsHtmlBodyEmpty(item.content || '');
}

const NEWS_CONTENT_EMOJI_FALLBACK =
  "'Segoe UI Emoji', 'Noto Color Emoji', 'Apple Color Emoji', 'Segoe UI', Arial, sans-serif";

/** SQLite / прокси иногда отдают поля в нижнем регистре или snake_case. */
function newsRowField(item, camel, lower, snake) {
  if (!item) return undefined;
  const a = item[camel];
  const b = item[lower];
  const c = snake != null ? item[snake] : undefined;
  const has = (v) => v != null && String(v).trim() !== '';
  if (has(a)) return a;
  if (has(b)) return b;
  if (snake != null && has(c)) return c;
  return snake != null ? a ?? b ?? c : a ?? b;
}

/** Опрос: voters = [{ userId, optionIndex }, …]. Возвращает индекс выбранного варианта или -1. */
function getUserPollVoteInfo(voters, userId) {
  if (userId == null) return { hasVoted: false, optionIdx: -1 };
  const uid = Number(userId);
  const list = Array.isArray(voters) ? voters : [];
  const entry = list.find(
    (v) =>
      v &&
      (Number(v.userId) === uid ||
        String(v.userId) === String(userId) ||
        v.userId === userId)
  );
  if (!entry) return { hasVoted: false, optionIdx: -1 };
  const raw = entry.optionIndex;
  const idx = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(idx) || idx < 0) return { hasVoted: false, optionIdx: -1 };
  return { hasVoted: true, optionIdx: idx };
}

/** Цвет текста для мета-строки: поле contentColor в API или первый inline-цвет из HTML (после foreColor в редакторе). */
function effectiveNewsContentColorForMeta(item) {
  if (!item) return '';
  const fromField = String(
    newsRowField(item, 'contentColor', 'contentcolor', 'content_color') || ''
  ).trim();
  if (fromField) return fromField;
  return extractFirstCssTextColorFromHtml(item.content || '');
}

/** Первые три целочисленных компонента rgb()/rgba() (в т.ч. «255 255 255» без запятых — как отдаёт часть браузеров). */
function rgbTripletFromCssColor(raw) {
  if (!raw || typeof raw !== 'string') return null;
  const m = raw.trim().match(/^rgba?\(\s*([^)]+)\)/i);
  if (!m) return null;
  const parts = m[1]
    .split(/[,/\s]+/)
    .map((p) => p.trim())
    .filter(Boolean);
  const nums = [];
  for (const p of parts) {
    if (/%$/.test(p)) continue;
    const n = parseInt(p, 10);
    if (!Number.isNaN(n)) nums.push(n);
  }
  if (nums.length < 3) return null;
  return [nums[0], nums[1], nums[2]];
}

/** Светлый ли заданный CSS-цвет (белый / почти белый) — для подстройки цвета мета-строки «Опубликовано». */
function isEssentiallyWhiteColor(val) {
  if (val == null || typeof val !== 'string') return false;
  const raw = val.trim();
  if (!raw) return false;
  const s = raw.toLowerCase().replace(/\s/g, '');
  if (s === 'white') return true;
  if (s === '#fff' || s === '#ffffff') return true;
  if (/^#[0-9a-f]{3}$/i.test(s)) {
    const r = parseInt(s[1] + s[1], 16);
    const g = parseInt(s[2] + s[2], 16);
    const b = parseInt(s[3] + s[3], 16);
    return r >= 245 && g >= 245 && b >= 245;
  }
  if (/^#[0-9a-f]{6}$/i.test(s)) {
    const r = parseInt(s.slice(1, 3), 16);
    const g = parseInt(s.slice(3, 5), 16);
    const b = parseInt(s.slice(5, 7), 16);
    return r >= 245 && g >= 245 && b >= 245;
  }
  if (/^#[0-9a-f]{8}$/i.test(s)) {
    const r = parseInt(s.slice(1, 3), 16);
    const g = parseInt(s.slice(3, 5), 16);
    const b = parseInt(s.slice(5, 7), 16);
    return r >= 245 && g >= 245 && b >= 245;
  }
  const triplet = rgbTripletFromCssColor(raw);
  if (triplet) {
    const [r, g, b] = triplet;
    return r >= 245 && g >= 245 && b >= 245;
  }
  const hsl = s.match(/^hsla?\(([^)]+)\)/i);
  if (hsl) {
    const parts = hsl[1].split(',').map((p) => p.trim());
    if (parts.length >= 3) {
      const lStr = parts[2].replace('%', '');
      const lum = parseFloat(lStr);
      if (!Number.isNaN(lum) && lum >= 98) return true;
    }
  }
  return false;
}

/** Цвет строки «Опубликовано» / «Запланировано»: при заданном contentColor совпадает с текстом; белый — чуть прозрачнее. */
function publicationDateLineColor({
  isCongrat,
  isModalMobileFeed,
  usePhotoCardLikeMobileChrome,
  rawContentColor,
}) {
  if (isModalMobileFeed && isCongrat) return 'rgba(255,247,237,0.7)';
  const cc = rawContentColor && String(rawContentColor).trim();
  if (cc) {
    if (isEssentiallyWhiteColor(cc)) return 'rgba(255,255,255,0.72)';
    return cc;
  }
  if (isModalMobileFeed) return '#94a3b8';
  if (usePhotoCardLikeMobileChrome) return '#94a3b8';
  return '#888';
}

function parseNewsGoogleFontFamilies(item) {
  const raw = newsRowField(item, 'newsGoogleFontFamilies', 'newsgooglefontfamilies');
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (typeof raw !== 'string') return [];
  try {
    let p = JSON.parse(raw);
    if (typeof p === 'string') {
      try {
        p = JSON.parse(p);
      } catch {
        return p.trim() ? [p.trim()] : [];
      }
    }
    return Array.isArray(p) ? p.filter(Boolean) : [];
  } catch {
    return [];
  }
}

/** Все семейства для подключения Google Fonts: из JSON + из title/content font-family (на случай пустого JSON). */
function collectAllNewsGoogleFontNames(item) {
  const fromJson = parseNewsGoogleFontFamilies(item);
  const tf = newsRowField(item, 'titleFontFamily', 'titlefontfamily');
  const cf = newsRowField(item, 'contentFontFamily', 'contentfontfamily');
  const fromStacks = collectGoogleFontNamesFromStacks(tf, cf);
  const seen = new Set();
  const out = [];
  for (const x of [...fromJson, ...fromStacks]) {
    const t = String(x).trim();
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(t);
  }
  return out;
}

function newsContentFontStack(item) {
  const raw = newsRowField(item, 'contentFontFamily', 'contentfontfamily');
  const base = raw && String(raw).trim();
  return base ? `${base}, ${NEWS_CONTENT_EMOJI_FALLBACK}` : NEWS_CONTENT_EMOJI_FALLBACK;
}

function getNewsSharedLocalFont(item) {
  const jsonRaw = newsRowField(item, 'newsLocalFontsJson', 'newslocalfontsjson');
  if (!jsonRaw) return null;
  try {
    const j = JSON.parse(jsonRaw);
    const sh = j.shared || (j.name && j.dataUrl ? j : null);
    if (sh?.name && sh?.dataUrl) return { name: String(sh.name), dataUrl: String(sh.dataUrl) };
  } catch {
    return null;
  }
  return null;
}

function escapeNewsLocalFontName(name) {
  return String(name || '').replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

// Styled components
const CommentModalBg = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 9999;
  background: rgba(34,40,49,0.82);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  height: 100vh;
`;

const CommentModal = styled.div`
  width: 100%;
  max-width: 730px;
  height: 600px;
  max-height: 190vh;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 8px 40px #2193b033, 0 0 0 2px #43e97b44;
  padding: 32px 28px 24px 28px;
  position: relative;
  font-family: inherit;
  color: #222;
  font-size: 1.08em;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const CommentInput = styled.textarea`
  width: 100%;
  min-height: 64px;
  max-height: 80px;
  border-radius: 10px;
  border: 1.5px solid #2193b0;
  font-size: 1.08em;
  padding: 12px 14px;
  margin-bottom: 12px;
  background: #f9f9f9;
  resize: none;
  overflow: hidden;
  box-sizing: border-box;
  &:focus { border: 2px solid #43e97b; outline: none; box-shadow: 0 0 8px #43e97b55; }
`;

const CommentsBlock = styled.div`
  margin-top: 16px;
  background: #f9f9f9;
  border-radius: 12px;
  box-shadow: 0 2px 8px #2193b011;
  padding: 14px 18px 8px 18px;
  overflow: hidden;
`;

const CommentItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #43e97b33;
  &:last-child { border-bottom: none; }
  font-family: 'Segoe UI Emoji', 'Noto Color Emoji', 'Apple Color Emoji', 'Segoe UI', 'Arial', sans-serif;
`;

const Wrapper = styled.div`
  flex: 1 1 0%;
  min-width: 0;
  min-height: 100vh;
  margin: 0;
  background: #f7fbff;
  padding: 32px 20px 28px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: auto;

  ${(p) =>
    p.$inModal &&
    `
    min-height: 0;
    background: transparent;
    padding: 0 0 28px 0;
    align-items: stretch;
    overflow: visible;
  `}
`;

const Title = styled.h2`
  color: #232931;
  font-size: 1.8em;
  font-weight: 800;
  margin-bottom: 18px;
  letter-spacing: 0.01em;
  text-align: center;
`;

const Input = styled.input`
  padding: 10px 14px;
  border-radius: 10px;
  border: 1.5px solid #2193b0;
  font-size: 1.08rem;
  background: rgba(255,255,255,0.92);
  transition: border .2s, box-shadow .2s;
  &:focus { border: 2px solid #43e97b; outline: none; box-shadow: 0 0 8px #43e97b55; }
`;

const Textarea = styled.textarea`
  padding: 10px 14px;
  border-radius: 10px;
  border: 1.5px solid #e74c3c;
  font-size: 1.08rem;
  background: rgba(255,255,255,0.92);
  min-height: 60px;
  resize: vertical;
  transition: border .2s, box-shadow .2s;
  &:focus { border: 2px solid #fcb69f; outline: none; box-shadow: 0 0 8px #fcb69f55; }
`;

const Button = styled.button`
  background: linear-gradient(135deg, #2193b0 0%, #43e97b 100%);
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 7px 14px;
  font-weight: 600;
  font-size: 0.93em;
  cursor: pointer;
  box-shadow: 0 2px 8px #43e97b22, 0 0 0 2px #2193b044;
  transition: background 0.2s, color 0.2s;
  outline: none;
  filter: drop-shadow(0 0 8px #2193b088);
  &:hover {
    background: linear-gradient(135deg, #43e97b 0%, #2193b0 100%);
    color: #fff;
    transform: scale(1.07);
    box-shadow: 0 4px 24px #43e97b88, 0 0 32px #2193b088;
    border-color: #43e97b;
    filter: drop-shadow(0 0 16px #43e97bcc);
  }
`;

const CardList = styled.ul`
  list-style: none;
  padding: 0;
  width: 100%;
  max-width: 960px;
  margin: 0 auto;

  ${(p) =>
    p.$modalMobile &&
    `
    max-width: 100%;
    margin: 0;
    padding: 0;
  `}
`;

const Card = styled.li`
  background: #0f172a;
  border: 1px solid #1f2937;
  border-radius: 16px;
  box-shadow: 0 10px 24px rgba(0,0,0,.25);
  margin-bottom: 18px;
  padding: 22px 24px 18px 24px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  font-size: 0.95em;
  transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
  position: relative;
  overflow: hidden;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  box-sizing: border-box;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 16px 36px rgba(0,0,0,.35);
    border-color: #334155;
  }
  @media (max-width: 768px) {
    padding: 16px 14px 14px 14px;
    margin-bottom: 14px;
    border-radius: 12px;
    font-size: 0.9em;
  }
`;

const CardInfo = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

/** Строка «Опубликовано» — !important, чтобы внешние span-правила не перебивали цвет. */
const PublicationMetaText = styled.span`
  color: ${(p) => p.$metaColor} !important;
`;

const CardTitle = styled.span`
  color: #f8fafc;
  font-weight: 900;
  font-size: 1.35em;
  letter-spacing: 0.02em;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  position: relative;
  padding-left: 16px;
  padding-right: 12px;
  padding-top: 8px;
  padding-bottom: 8px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(36,48,65,.92) 0%, rgba(17,24,39,.92) 100%);
  border: 1px solid #243041;
  box-shadow: 0 6px 20px rgba(0,0,0,.25);
  &::before {
    content: '';
    position: absolute;
    left: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--accent, #38bdf8);
    box-shadow: 0 0 0 3px rgba(56,189,248,.15);
  }
`;

const CardContent = styled.div`
  color: #232931;
  font-size: 1.12em;
  margin-top: 2px;
  line-height: 1.6;
  & p,
  & span,
  & li,
  & div {
    font-family: inherit;
  }
  /* Цвет текста новости (contentColor): наследование от корня с inline-style, иначе перебивают span из редактора. */
  ${(p) =>
    p.$customContentColor
      ? `
    & p, & span, & li, & div {
      color: inherit !important;
    }
    & a {
      color: inherit !important;
      text-decoration: underline;
      text-underline-offset: 2px;
      opacity: 0.92;
    }
  `
      : ''}
  /* Inline font-family из редактора перебивает inherit; !important наследует шрифт карточки (в т.ч. Google). */
  & *:not(pre):not(code):not(kbd):not(samp) {
    font-family: inherit !important;
  }
`;

/** Текст новости в мобильной модалке: тёмная «стеклянная» карточка, читаемый контраст. */
const ModalMobileHtmlBody = styled.div`
  font-size: 1.02em;
  line-height: 1.65;
  margin-top: 10px;
  word-break: break-word;
  color: #e2e8f0;
  ${(p) =>
    p.$customContentColor
      ? `
    color: inherit;
    & p, & span, & li, & div {
      color: inherit !important;
      font-family: inherit;
    }
    & a {
      color: inherit !important;
      text-decoration: underline;
      text-underline-offset: 2px;
      opacity: 0.92;
      font-family: inherit;
    }
  `
      : `
    & p, & span, & li, & div {
      color: #e2e8f0 !important;
      font-family: inherit;
    }
    & a {
      color: #7dd3fc !important;
      font-family: inherit;
    }
  `}
  & *:not(pre):not(code):not(kbd):not(samp) {
    font-family: inherit !important;
  }
`;

const ModalMobileHtmlBodyWarm = styled(ModalMobileHtmlBody)`
  color: #292524;
  & p,
  & span,
  & li,
  & div {
    color: #292524 !important;
    font-family: inherit;
  }
  & *:not(pre):not(code):not(kbd):not(samp) {
    font-family: inherit !important;
  }
  & a {
    color: #0c4a6e !important;
    font-family: inherit;
  }
`;

const DeleteBtn = styled(Button)`
  background: linear-gradient(135deg, #e74c3c 0%, #fcb69f 100%);
  color: #fff;
  margin-left: 18px;
  filter: drop-shadow(0 0 8px #e74c3c88);
  &:hover {
    background: linear-gradient(135deg, #fcb69f 0%, #e74c3c 100%);
    filter: drop-shadow(0 0 16px #e74c3ccc);
  }
`;

// Сдержанный стиль для варианта опроса
const CardOption = styled.div`
  background: #f7f9fc;
  border: 1px solid #e5eaf0;
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 14px;
  color: #2c3e50;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  position: relative;
  display: block;
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  box-sizing: border-box;
  &:hover {
    border-color: #d7dde6;
    box-shadow: 0 4px 14px rgba(16, 24, 40, 0.08);
  }
  @media (max-width: 768px) {
    padding: 12px 14px;
    margin-bottom: 10px;
    border-radius: 10px;
  }
`;

// Main component
export default function NewsFeed({ token, modal = false }) {
  // State declarations
  const [news, setNews] = useState([]);
  const [votersInfo, setVotersInfo] = useState({});
  const [commentModal, setCommentModal] = useState({ open: false, congratId: null, newsId: null });
  const [commentText, setCommentText] = useState('');
  const [showEmoji, setShowEmoji] = useState(false);
  const [congratulations, setCongratulations] = useState([]);
  const commentEditorRef = useRef(null);

  const tokenizeFromHtml = (html) => {
    if (!html) return '';
    const container = document.createElement('div');
    container.innerHTML = html;
    // replace inline custom emoji images back to tokens
    container.querySelectorAll('img[data-custom-emoji]')?.forEach(img => {
      const token = img.getAttribute('data-token');
      const textNode = document.createTextNode(token || '');
      img.replaceWith(textNode);
    });
    return container.textContent || container.innerText || '';
  };

  /** Вставка текста/юникод-эмодзи в contentEditable после выбора из пикера (execCommand часто ломается без фокуса/Range). */
  const insertPlainTextInCommentEditor = (raw) => {
    const el = commentEditorRef.current;
    if (!el) return;
    const text = String(raw || '');
    el.focus();
    try {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        if (el.contains(range.commonAncestorContainer)) {
          range.deleteContents();
          const node = document.createTextNode(text);
          range.insertNode(node);
          range.setStartAfter(node);
          range.collapse(true);
          sel.removeAllRanges();
          sel.addRange(range);
          const synced = tokenizeFromHtml(el.innerHTML);
          setCommentText(synced.slice(0, MAX_COMMENT_LEN));
          return;
        }
      }
    } catch (_) { /* fallback */ }
    el.appendChild(document.createTextNode(text));
    try {
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    } catch (_) { /* ignore */ }
    const synced = tokenizeFromHtml(el.innerHTML);
    setCommentText(synced.slice(0, MAX_COMMENT_LEN));
  };

  const getRangeInsideCommentEditor = () => {
    const el = commentEditorRef.current;
    if (!el) return null;
    el.focus();
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      if (el.contains(range.commonAncestorContainer)) return range;
    }
    if (!sel) return null;
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);
    return sel.getRangeAt(0);
  };

  const insertCustomEmojiAtCursor = (token) => {
    if (!commentEditorRef.current) return;
    const el = commentEditorRef.current;
    const url = customEmojiMap[token];
    if (!url) {
      insertPlainTextInCommentEditor(token);
      return;
    }
    const img = document.createElement('img');
    img.src = url;
    img.alt = token;
    img.setAttribute('data-custom-emoji', 'true');
    img.setAttribute('data-token', token);
    const px = Number((commentModal.congratId ? emojiSizes.congratsComments : emojiSizes.newsComments) || 24);
    img.style.width = px + 'px';
    img.style.height = px + 'px';
    img.style.objectFit = 'cover';
    img.style.verticalAlign = 'middle';
    img.style.margin = '0 2px';
    img.style.borderRadius = '6px';
    const range = getRangeInsideCommentEditor();
    if (range) {
      range.deleteContents();
      range.insertNode(img);
      range.setStartAfter(img);
      range.collapse(true);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    } else {
      el.appendChild(img);
    }
    const text = tokenizeFromHtml(el.innerHTML);
    setCommentText(text.slice(0, MAX_COMMENT_LEN));
  };

  const [form, setForm] = useState({ title: '', content: '', publishAt: '' });
  const [pollVoteLoading, setPollVoteLoading] = useState({});
  const [pollVoteError, setPollVoteError] = useState({});
  const [likeLoading, setLikeLoading] = useState({});
  const [createPollOpen, setCreatePollOpen] = useState(false);
  const [pollForm, setPollForm] = useState({
    question: '',
    options: ['', ''],
    cardBackground: '',
    cardImageUrl: '',
    cardImageUrlMobile: '',
  });
  const [pollCreateLoading, setPollCreateLoading] = useState(false);
  const [pollCreateError, setPollCreateError] = useState('');
  const [pollCardImageUploadingDesktop, setPollCardImageUploadingDesktop] = useState(false);
  const [pollCardImageUploadingMobile, setPollCardImageUploadingMobile] = useState(false);
  const pollCardImageInputRefDesktop = useRef(null);
  const pollCardImageInputRefMobile = useRef(null);

  const resetPollFormFields = useCallback(() => {
    setPollForm({
      question: '',
      options: ['', ''],
      cardBackground: '',
      cardImageUrl: '',
      cardImageUrlMobile: '',
    });
    if (pollCardImageInputRefDesktop.current) pollCardImageInputRefDesktop.current.value = '';
    if (pollCardImageInputRefMobile.current) pollCardImageInputRefMobile.current.value = '';
  }, []);

  const closeCreatePollModal = useCallback(() => {
    setCreatePollOpen(false);
    setPollCreateError('');
    resetPollFormFields();
  }, [resetPollFormFields]);

  const handlePollCardImageFile = useCallback(
    async (e, variant) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file || !String(file.type || '').startsWith('image/')) {
        alert('Выберите файл изображения');
        return;
      }
      if (!token) {
        alert('Нужна авторизация');
        return;
      }
      const setBusy =
        variant === 'mobile' ? setPollCardImageUploadingMobile : setPollCardImageUploadingDesktop;
      setBusy(true);
      try {
        const fd = new FormData();
        fd.append('image', file);
        const res = await fetch('/api/news/card-image', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Ошибка загрузки');
        if (data.url) {
          if (variant === 'mobile') {
            setPollForm((p) => ({ ...p, cardImageUrlMobile: data.url }));
          } else {
            setPollForm((p) => ({ ...p, cardImageUrl: data.url }));
          }
        }
      } catch (err) {
        alert(err.message || 'Не удалось загрузить изображение');
      } finally {
        setBusy(false);
      }
    },
    [token]
  );
  const [collapsedVoters, setCollapsedVoters] = useState({});
  const [showContentModal, setShowContentModal] = useState(false);
  const [tempContent, setTempContent] = useState('');
  const [sort, setSort] = useState('desc');
  const [user, setUser] = useState(null);
  const [isCommentSending, setIsCommentSending] = useState(false);
  const [commentError, setCommentError] = useState('');
  const [customEmojiMap, setCustomEmojiMap] = useState({});
  const [emojiSizes, setEmojiSizes] = useState({
    news: getCustomEmojiSizeForArea('news'),
    newsComments: getCustomEmojiSizeForArea('newsComments'),
    congrats: getCustomEmojiSizeForArea('congrats'),
    congratsComments: getCustomEmojiSizeForArea('congratsComments'),
    chatModal: getCustomEmojiSizeForArea('chatModal')
  });
  // Refs for keeping focus on the commented item
  const itemRefs = useRef({});
  const MAX_COMMENT_LEN = 500;
  // Helper to extract avatar url from different payload shapes
  // Backend origin resolver for static files
  const getApiOrigin = () => {
    // Allow global override or env var; fallback to window.location.origin
    const g = (typeof window !== 'undefined' && (window.__API_ORIGIN__ || window.API_ORIGIN)) || '';
    const env = (typeof process !== 'undefined' && process.env && (process.env.REACT_APP_API_ORIGIN || process.env.VITE_API_ORIGIN)) || '';
    return g || env || (typeof window !== 'undefined' && window.location ? window.location.origin : '');
  };

  const getAvatarUrl = (src) => {
    if (!src) return null;
    if (typeof src === 'string') return normalizeAvatarUrl(src);
    if (typeof src !== 'object') return null;
    return (
      normalizeAvatarUrl(src.avatar_url) ||
      normalizeAvatarUrl(src.authorAvatar) ||
      normalizeAvatarUrl(src.author_avatar) ||
      normalizeAvatarUrl(src.userAvatar) ||
      normalizeAvatarUrl(src.user_avatar) ||
      (src.employee && normalizeAvatarUrl(src.employee.avatar_url)) ||
      (src.author && (normalizeAvatarUrl(src.author.avatar_url) || normalizeAvatarUrl(src.author.avatar))) ||
      // Fallback: if this item authored by current user, use their avatar from localStorage
      ((src.authorId && user && (src.authorId === user.id) && normalizeAvatarUrl(user.avatar_url)) || null) ||
      null
    );
  };
  // For congratulations entries coming from /api/congratulations,
  // get author's avatar from the congratulation data itself
  const getAuthorAvatarForCongrat = (congrat) => {
    if (congrat && congrat.author_avatar) {
      return normalizeAvatarUrl(congrat.author_avatar);
    }
    // Fallback: try to find corresponding news item to get author's avatar
    const relatedNews = Array.isArray(news) ? news.find(n => n && n.congratulationId === congrat.id) : null;
    return relatedNews ? getAvatarUrl(relatedNews) : null;
  };
  // Normalize avatar url helper
  const normalizeAvatarUrl = (url) => {
    if (!url || typeof url !== 'string') return null;
    let trimmed = url.trim();
    // Normalize Windows backslashes to forward slashes
    trimmed = trimmed.replace(/\\/g, '/');
    // If absolute local path contains uploads/avatars, strip to web path
    const idx = trimmed.toLowerCase().indexOf('/uploads/avatars/');
    if (idx !== -1) {
      const path = trimmed.slice(idx);
      // If running on different origin (e.g., 3000), prefix backend origin
      if (typeof window !== 'undefined' && window.location && !path.startsWith('http')) {
        return `${getApiOrigin()}${path}`;
      }
      return path;
    }
    if (!trimmed) return null;
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
    if (trimmed.startsWith('/uploads') || trimmed.startsWith('/images')) {
      return `${getApiOrigin()}${trimmed}`;
    }
    if (trimmed.startsWith('uploads/')) return `${getApiOrigin()}/` + trimmed;
    return `${getApiOrigin()}/uploads/avatars/` + trimmed.replace(/^\/+/, '');
  };

  /** /uploads/... с API-origin (как аватары) — иначе на десктопе с другого порта фото карточки не грузится. */
  const resolveUploadsUrl = (url) => {
    if (!url || typeof url !== 'string') return '';
    const t = url.trim();
    if (!t) return '';
    if (t.startsWith('http://') || t.startsWith('https://')) return t;
    if (t.startsWith('/uploads/')) return `${getApiOrigin()}${t}`;
    return t;
  };

  // Derived values
  const userId = user?.id;
  const isAdminOrHr = user?.role === 'admin' || user?.role === 'hr';
  const now = Date.now();
  
  // Определяем, мобильное ли устройство
  const isMobileDevice = typeof window !== 'undefined' && (window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
  const isModalMobileFeed = modal && isMobileDevice;

  // Effect: Lock body scroll when comment modal is open
  useEffect(() => {
    if (commentModal.open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [commentModal.open]);

  // Load custom emojis map for rendering tokens like custom:name
  useEffect(() => {
    fetch('/api/emojis/list')
      .then(r => r.json())
      .then(list => {
        const map = {};
        (Array.isArray(list) ? list : []).forEach(e => { map[`custom:${e.name}`] = e.url; });
        setCustomEmojiMap(map);
      })
      .catch(() => {});
  }, []);

  // Подписка на обновления настроек эмодзи для динамического изменения размеров
  useEffect(() => {
    const unsubscribe = subscribeEmojiSettings(() => {
      setEmojiSizes({
        news: getCustomEmojiSizeForArea('news'),
        newsComments: getCustomEmojiSizeForArea('newsComments'),
        congrats: getCustomEmojiSizeForArea('congrats'),
        congratsComments: getCustomEmojiSizeForArea('congratsComments'),
        chatModal: getCustomEmojiSizeForArea('chatModal')
      });
    });
    return unsubscribe;
  }, []);

  const renderTextWithCustomEmojis = (text, area = 'news') => {
    if (typeof text !== 'string' || !text) return text;
    const parts = text.split(/(custom:[\w.-]+)/g);
    return parts.map((part, idx) => {
      if (/^custom:[\w.-]+$/.test(part) && customEmojiMap[part]) {
        const px = Number(
          area === 'news' ? emojiSizes.news
          : area === 'newsComments' ? emojiSizes.newsComments
          : area === 'congrats' ? emojiSizes.congrats
          : area === 'congratsComments' ? emojiSizes.congratsComments
          : emojiSizes.news
        ) || 24;
        return (
          <img
            key={`ce-${idx}`}
            src={customEmojiMap[part]}
            alt={part}
            style={{ width: px, height: px, objectFit: 'cover', verticalAlign: 'middle', margin: '0 2px', borderRadius: 6 }}
          />
        );
      }
      return <span key={`t-${idx}`}>{part}</span>;
    });
  };

  // Effect: Setup socket listener (используем общий socket)
  useEffect(() => {
    // Используем общий socket из SocketProvider вместо создания нового
    const socket = window.socket;
    if (!socket || !socket.connected) return;

    const handleNewCongratulation = (congrat) => {
      setCongratulations(prev => [congrat, ...prev]);
    };
    
    const handleCongratulationDeleted = ({ id }) => {
      setCongratulations(prev => prev.filter(c => c.id !== id));
    };
    
    const handleNewsDeleted = ({ id }) => {
      setNews(prev => prev.filter(n => n.id !== id));
    };

    const handleNewsPollVoted = ({ newsId, votes, voters }) => {
      const nid = newsId != null ? Number(newsId) : NaN;
      setNews((prev) =>
        prev.map((n) => {
          const same = n.id === newsId || n.id === nid || String(n.id) === String(newsId);
          if (!same) return n;
          return { ...n, votes: Array.isArray(votes) ? votes : n.votes, voters: Array.isArray(voters) ? voters : n.voters };
        })
      );
      setVotersInfo((prev) => {
        const next = { ...prev };
        delete next[newsId];
        if (!Number.isNaN(nid)) delete next[nid];
        return next;
      });
    };

    socket.on('new_congratulation', handleNewCongratulation);
    socket.on('congratulation-deleted', handleCongratulationDeleted);
    socket.on('news-deleted', handleNewsDeleted);
    socket.on('news-poll-voted', handleNewsPollVoted);

    return () => {
      if (socket) {
        socket.off('new_congratulation', handleNewCongratulation);
        socket.off('congratulation-deleted', handleCongratulationDeleted);
        socket.off('news-deleted', handleNewsDeleted);
        socket.off('news-poll-voted', handleNewsPollVoted);
      }
    };
  }, []);

  // Effect: Fetch congratulations when token is available
  useEffect(() => {
    if (!token) return;
    
    fetch('/api/congratulations', { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setCongratulations(data);
        } else {
          console.error('API returned non-array data:', data);
          setCongratulations([]);
        }
      })
      .catch((error) => {
        console.error('Error fetching congratulations:', error);
        setCongratulations([]);
      });
  }, [token]);

  // Effect: Fetch news periodically and user from localStorage
  useEffect(() => {
    const fetchNews = () => {
      fetch('/api/news', { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(newsArr => {
          if (Array.isArray(newsArr)) {
            setNews(newsArr);
            if (newsArr.length > 0) {
              localStorage.setItem('lastNewsId', newsArr[0].id);
              window.dispatchEvent(new CustomEvent('news-read'));
            }
          } else {
            setNews([]);
          }
        })
        .catch(() => setNews([]));
    };
    fetchNews();
    const interval = setInterval(fetchNews, 15000);
    const u = localStorage.getItem('user');
    if (u) setUser(JSON.parse(u));
    const handler = () => fetchNews();
    window.addEventListener('news-published', handler);
    return () => {
      clearInterval(interval);
      window.removeEventListener('news-published', handler);
    };
  }, [token]);

  // Effect: Load voters info for polls
  useEffect(() => {
    const pollNews = news.filter(n => n.type === 'poll' || !!n.pollOptions);
    pollNews.forEach(item => {
      fetch(`/api/news/${item.id}/voters`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(data => {
          setVotersInfo(prev => ({ ...prev, [item.id]: data }));
        })
        .catch(() => {
          setVotersInfo(prev => ({ ...prev, [item.id]: { voted: [], notVoted: [], totalWorkUsers: 0 } }));
        });
    });
  }, [news, token]);

  useLayoutEffect(() => {
    const families = new Set();
    const cssUrls = [];
    news.forEach((item) => {
      collectAllNewsGoogleFontNames(item).forEach((f) => families.add(f));
      const u = newsRowField(item, 'newsCustomFontCssUrl', 'newscustomfontcssurl');
      const url = u && String(u).trim();
      if (url) cssUrls.push(url);
    });
    syncNewsGoogleFontLinkElements([...families], { preview: false });
    syncNewsCustomCssLinks(cssUrls, { preview: false });
  }, [news]);

  useEffect(() => {
    const wantIds = new Set();
    news.forEach((item) => {
      const loc = getNewsSharedLocalFont(item);
      if (!loc) return;
      const id = String(item.id);
      wantIds.add(id);
      if (document.querySelector(`style[data-news-local-font-id="${id}"]`)) return;
      const style = document.createElement('style');
      style.setAttribute('data-news-local-font-id', id);
      const n = escapeNewsLocalFontName(loc.name);
      style.textContent = `@font-face{font-family:'${n}';src:url(${JSON.stringify(loc.dataUrl)});font-display:swap;}`;
      document.head.appendChild(style);
    });
    document.querySelectorAll('style[data-news-local-font-id]').forEach((el) => {
      const id = el.getAttribute('data-news-local-font-id');
      if (id && !wantIds.has(id)) el.remove();
    });
  }, [news]);

  useEffect(() => {
    return () => {
      document.querySelectorAll('link[data-news-google-font-app]').forEach((el) => el.remove());
      document.querySelectorAll('link[data-news-custom-css-app]').forEach((el) => el.remove());
      document.querySelectorAll('style[data-news-local-font-id]').forEach((el) => el.remove());
    };
  }, []);

  // Handlers
  const handleLike = (id, isNews = false, userLiked = false) => {
    if (!userId || likeLoading[id]) return; // Предотвращаем множественные клики
    
    // Устанавливаем состояние загрузки
    setLikeLoading(prev => ({ ...prev, [id]: true }));
    
    // Оптимистичное обновление UI - сразу меняем состояние локально
    if (isNews) {
      setNews(prevNews => 
        prevNews.map(item => 
          item.id === id 
            ? { 
                ...item, 
                likes: userLiked ? (item.likes || 0) - 1 : (item.likes || 0) + 1,
                newsLikes: userLiked 
                  ? (item.newsLikes || []).filter(like => like.user_id !== userId)
                  : [...(item.newsLikes || []), { user_id: userId }]
              }
            : item
        )
      );
    } else {
      setCongratulations(prevCongrats => 
        prevCongrats.map(item => 
          item.id === id 
            ? { 
                ...item, 
                likes: userLiked ? (item.likes || 0) - 1 : (item.likes || 0) + 1,
                likedByUser: !userLiked
              }
            : item
        )
      );
    }

    // Отправляем запрос на сервер
    const url = isNews
      ? (userLiked ? `/api/news/${id}/unlike` : `/api/news/${id}/like`)
      : `/api/congratulations/${id}/like`;

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({})
    }).then(response => {
      setLikeLoading(prev => ({ ...prev, [id]: false })); // Снимаем состояние загрузки
      
      if (!response.ok) {
        // Если запрос не удался, откатываем изменения
        if (isNews) {
          setNews(prevNews => 
            prevNews.map(item => 
              item.id === id 
                ? { 
                    ...item, 
                    likes: userLiked ? (item.likes || 0) + 1 : (item.likes || 0) - 1,
                    newsLikes: userLiked 
                      ? [...(item.newsLikes || []), { user_id: userId }]
                      : (item.newsLikes || []).filter(like => like.user_id !== userId)
                  }
                : item
            )
          );
        } else {
          setCongratulations(prevCongrats => 
            prevCongrats.map(item => 
              item.id === id 
                ? { 
                    ...item, 
                    likes: userLiked ? (item.likes || 0) + 1 : (item.likes || 0) - 1,
                    likedByUser: userLiked
                  }
                : item
            )
          );
        }
        console.error('Ошибка при обновлении лайка');
      }
    }).catch(error => {
      setLikeLoading(prev => ({ ...prev, [id]: false })); // Снимаем состояние загрузки
      
      // Если произошла ошибка, откатываем изменения
      if (isNews) {
        setNews(prevNews => 
          prevNews.map(item => 
            item.id === id 
              ? { 
                  ...item, 
                  likes: userLiked ? (item.likes || 0) + 1 : (item.likes || 0) - 1,
                  newsLikes: userLiked 
                    ? [...(item.newsLikes || []), { user_id: userId }]
                    : (item.newsLikes || []).filter(like => like.user_id !== userId)
                }
              : item
          )
        );
      } else {
        setCongratulations(prevCongrats => 
          prevCongrats.map(item => 
            item.id === id 
              ? { 
                  ...item, 
                  likes: userLiked ? (item.likes || 0) + 1 : (item.likes || 0) - 1,
                  likedByUser: userLiked
                }
              : item
          )
        );
      }
      console.error('Ошибка при обновлении лайка:', error);
    });
  };

  // Удаление новости или голосования
  const handleDeleteNews = async (newsId) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту публикацию?')) {
      return;
    }
    
    try {
      const response = await fetch(`/api/news/${newsId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Ошибка при удалении');
      }
      
      // Удаляем из локального состояния
      setNews(prev => prev.filter(n => n.id !== newsId));
      
      // Emit socket событие для обновления у других клиентов
      if (window.socket && window.socket.connected) {
        window.socket.emit('news-deleted', { id: newsId });
      }
    } catch (error) {
      console.error('Ошибка при удалении новости:', error);
      alert('Не удалось удалить публикацию: ' + error.message);
    }
  };

  // Удаление поздравления
  const handleDeleteCongrat = async (congratId) => {
    if (!window.confirm('Вы уверены, что хотите удалить это поздравление?')) {
      return;
    }
    
    try {
      const response = await fetch(`/api/congratulations/${congratId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Ошибка при удалении');
      }
      
      // Удаляем из локального состояния
      setCongratulations(prev => prev.filter(c => c.id !== congratId));
      
      // Emit socket событие для обновления у других клиентов
      if (window.socket && window.socket.connected) {
        window.socket.emit('congratulation-deleted', { id: congratId });
      }
    } catch (error) {
      console.error('Ошибка при удалении поздравления:', error);
      alert('Не удалось удалить поздравление: ' + error.message);
    }
  };

  // Fix local comment addition in handleComment to ensure username and text are strings
  const handleComment = (id, commentText, isNews = false) => {
    setIsCommentSending(true);
    setCommentError('');

    const url = isNews
      ? `/api/news/${id}/comment`
      : `/api/congratulations/${id}/comment`;

    let body;
    if (isNews) {
      body = JSON.stringify({ commentText });
    } else {
      body = JSON.stringify({ commentText });
    }

    const fetchOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body
    };

    fetch(url, fetchOptions)
      .then(res => {
        if (!res.ok) throw new Error('Ошибка при отправке комментария');
        setCommentText('');

        // Normalize commentText and username to strings
        const normalizedText = String(commentText);
        const normalizedUsername = String(user?.username || '');

        // Locally update comments for instant display
        if (isNews) {
          setNews(prevNews => prevNews.map(n =>
            n.id === id
              ? {
                  ...n,
                  newsComments: [...(n.newsComments || []), { username: normalizedUsername, text: normalizedText, avatar: user?.avatarUrl || null }]
                }
              : n
          ));
        } else {
          setCongratulations(prev => prev.map(c =>
            c.id === id
              ? {
                  ...c,
                  comments: [...(c.comments || []), { username: normalizedUsername, text: normalizedText, avatar: user?.avatarUrl || null }]
                }
              : c
          ));
        }

        // Keep focus on the commented card
        const key = isNews ? `news-${id}` : `congrat-${id}`;
        const doScroll = () => {
          const el = itemRefs.current[key];
          if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        };
        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
          window.requestAnimationFrame(doScroll);
        } else {
          setTimeout(doScroll, 0);
        }

        // Close comment modal after successful send
        setCommentModal({ open: false, congratId: null, newsId: null });
      })
      .catch(() => setCommentError('Не удалось отправить комментарий.'))
      .finally(() => setIsCommentSending(false));
  };

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const addPollOption = () => {
    setPollForm(p => ({ ...p, options: [...p.options, ''] }));
  };
  const removePollOption = (idx) => {
    setPollForm(p => ({ ...p, options: p.options.filter((_, i) => i !== idx) }));
  };
  const updatePollOption = (idx, value) => {
    setPollForm(p => ({ ...p, options: p.options.map((opt, i) => i === idx ? value : opt) }));
  };
  const submitPoll = async () => {
    setPollCreateError('');
    const question = pollForm.question.trim();
    const options = pollForm.options.map(o => o.trim()).filter(Boolean);
    if (!question || options.length < 2) {
      setPollCreateError('Введите вопрос и минимум 2 варианта.');
      return;
    }
    setPollCreateLoading(true);
    try {
      const res = await fetch('/api/news/poll', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          question,
          options,
          cardBackground: pollForm.cardBackground?.trim() || undefined,
          cardImageUrl: pollForm.cardImageUrl?.trim() || undefined,
          cardImageUrlMobile: pollForm.cardImageUrlMobile?.trim() || undefined,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || 'Ошибка создания опроса');
      }
      closeCreatePollModal();
      window.dispatchEvent(new CustomEvent('news-published'));
      fetch('/api/news', { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(newsArr => { if (Array.isArray(newsArr)) setNews(newsArr); });
    } catch (e) {
      setPollCreateError(e.message || 'Ошибка создания опроса');
    } finally {
      setPollCreateLoading(false);
    }
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (e.target && e.target.name === 'plan' && !form.publishAt) {
      alert('Укажите дату публикации для планирования!');
      return;
    }
    
    fetch('/api/news', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ title: form.title, content: form.content, publishAt: form.publishAt })
    })
      .then(() => {
        setForm({ title: '', content: '', publishAt: '' });
        fetch('/api/news', { headers: { Authorization: `Bearer ${token}` } })
          .then(r => r.json())
          .then(newsArr => {
            if (Array.isArray(newsArr)) {
              setNews(newsArr);
              if (newsArr.length > 0) {
                localStorage.setItem('lastNewsId', newsArr[0].id);
                window.dispatchEvent(new CustomEvent('news-read'));
              }
            } else {
              setNews([]);
            }
          })
          .catch(() => setNews([]));
      });
  };

  // --- Для голосования в опросах новостей ---
  const handlePollVote = async (newsId, optionIdx) => {
    if (!userId) return;
    setPollVoteLoading(prev => ({ ...prev, [newsId]: true }));
    setPollVoteError(prev => ({ ...prev, [newsId]: '' }));
    
    try {
      const res = await fetch(`/api/news/${newsId}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ optionIndex: optionIdx }),
      });
      
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || 'Ошибка голосования');
      }
      
      // После голосования обновляем новости
      fetch('/api/news', { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(newsArr => {
          if (Array.isArray(newsArr)) setNews(newsArr);
        });
    } catch (e) {
      setPollVoteError(prev => ({ ...prev, [newsId]: e.message || 'Ошибка голосования' }));
    } finally {
      setPollVoteLoading(prev => ({ ...prev, [newsId]: false }));
    }
  };

  // Переключение состояния свернутости для конкретного варианта
  const toggleVotersCollapse = (newsId, optionIdx) => {
    setCollapsedVoters(prev => ({
      ...prev,
      [newsId]: {
        ...(prev[newsId] || {}),
        [optionIdx]: !((prev[newsId] || {})[optionIdx])
      }
    }));
  };

  // Sorting and filtering
  const sortedNews = [...news].sort((a, b) => sort === 'desc' ? b.id - a.id : a.id - b.id);
  const visibleNews = modal
    ? sortedNews
    : sortedNews.filter(n => isAdminOrHr || (!n.publishAt || new Date(n.publishAt) <= now));

  // Unified feed (news + congratulations) for modal view, newest-first by default
  const combinedFeed = React.useMemo(() => {
    // normalize timestamp for sorting
    const mapNews = (arr) => {
      if (!Array.isArray(arr)) return [];
      return arr.map(n => ({
        kind: 'news',
        ts: (n.publishAt ? Date.parse(n.publishAt) : (n.createdAt ? Date.parse(n.createdAt) : 0)) || n.id || 0,
        item: n
      }));
    };
    const mapCongrats = (arr) => {
      if (!Array.isArray(arr)) return [];
      return arr.map(c => ({
        kind: 'congrat',
        ts: (c.scheduled_at ? Date.parse(c.scheduled_at) : (c.created_at ? Date.parse(c.created_at) : 0)) || c.id || 0,
        item: c
      }));
    };

    const baseNews = modal ? news : visibleNews;
    const unified = [...mapNews(baseNews), ...mapCongrats(congratulations)];
    unified.sort((a, b) => (sort === 'desc' ? b.ts - a.ts : a.ts - b.ts));
    return unified;
  }, [modal, news, visibleNews, congratulations, sort]);

  // Selected item for comment modal header
  const currentCommentTarget = React.useMemo(() => {
    if (!commentModal.open) return null;
    if (commentModal.congratId) {
      return { kind: 'congrat', data: congratulations.find(c => c.id === commentModal.congratId) };
    }
    if (commentModal.newsId) {
      return { kind: 'news', data: news.find(n => n.id === commentModal.newsId) };
    }
    return null;
  }, [commentModal, news, congratulations]);

  return (
    <Wrapper $inModal={modal}>
      {!modal && <Title>Новостная лента</Title>}
      <div
        style={{
          display: 'flex',
          gap: isModalMobileFeed ? 8 : 12,
          marginBottom: isModalMobileFeed ? 12 : 18,
          alignItems: 'center',
          flexWrap: isModalMobileFeed ? 'wrap' : 'nowrap',
          width: '100%',
          justifyContent: isModalMobileFeed ? 'space-between' : 'flex-start',
        }}
      >
        <div style={{ display: 'flex', gap: isModalMobileFeed ? 8 : 12, alignItems: 'center', flex: isModalMobileFeed ? 1 : undefined, minWidth: 0 }}>
          <FaSort title="Сортировка" style={{ color: isModalMobileFeed ? '#94a3b8' : '#e74c3c', flexShrink: 0 }} />
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            style={{
              padding: isModalMobileFeed ? '8px 10px' : 6,
              borderRadius: isModalMobileFeed ? 12 : 6,
              border: isModalMobileFeed ? 'none' : '1px solid #b2bec3',
              background: isModalMobileFeed ? 'rgba(255,255,255,0.08)' : '#fff',
              color: isModalMobileFeed ? '#e2e8f0' : '#232931',
              fontSize: isModalMobileFeed ? '0.9em' : 'inherit',
              flex: 1,
              minWidth: 0,
            }}
            title="Сортировка"
          >
            <option value="desc">Сначала новые</option>
            <option value="asc">Сначала старые</option>
          </select>
        </div>
        {!isModalMobileFeed && (
          <span style={{ marginLeft: 16, color: '#636e72', fontSize: '0.98em' }}>Всего: {visibleNews.length}</span>
        )}
        {isModalMobileFeed && (
          <span style={{ color: '#94a3b8', fontSize: '0.82em', whiteSpace: 'nowrap' }}>{visibleNews.length} записей</span>
        )}
      </div>

      {/* Modal for editing news content */}
      {showContentModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, background: 'rgba(34,40,49,0.82)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowContentModal(false)}>
          <div style={{ width: '100%', maxWidth: 'none', minHeight: '100vh', height: '100%', background: '#fff', borderRadius: 0, padding: '48px 48px 32px 48px', boxSizing: 'border-box', position: 'relative', fontFamily: 'inherit', color: '#222', fontSize: '1.08em', overflow: 'hidden', overflowY: 'hidden', border: 'none' }} onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowContentModal(false)} style={{ position: 'absolute', top: 18, right: 24, fontSize: 28, background: 'none', border: 'none', cursor: 'pointer', color: '#2193b0' }}>×</button>
            <h3 style={{ marginBottom: 18, color: '#e74c3c' }}>Текст новости</h3>
            <Textarea
              name="modalContent"
              placeholder="Введите текст новости..."
              value={tempContent}
              onChange={e => setTempContent(e.target.value)}
              style={{ width: '100%', minHeight: 320, fontSize: '1.18em', padding: '18px 22px', resize: 'vertical' }}
              autoFocus
            />
            <Button type="button" style={{ marginTop: 18 }} onClick={() => { setForm(f => ({ ...f, content: tempContent })); setShowContentModal(false); }}>Сохранить</Button>
          </div>
        </div>
      )}

      {/* Create Poll Modal */}
      {isAdminOrHr && createPollOpen && (
        <CommentModalBg onClick={closeCreatePollModal}>
          <CommentModal onClick={e => e.stopPropagation()}>
            <button onClick={closeCreatePollModal} style={{ position: 'absolute', top: 12, right: 16, fontSize: 24, background: 'none', border: 'none', cursor: 'pointer', color: '#2193b0' }}>×</button>
            <h3 style={{ marginBottom: 14, color: '#e74c3c' }}>Создать опрос</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <Input
                placeholder="Вопрос опроса"
                value={pollForm.question}
                onChange={e => setPollForm(p => ({ ...p, question: e.target.value }))}
                style={{ fontSize: '1.08em' }}
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {pollForm.options.map((opt, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Input
                      placeholder={`Вариант #${idx + 1}`}
                      value={opt}
                      onChange={e => updatePollOption(idx, e.target.value)}
                      style={{ flex: 1 }}
                    />
                    {pollForm.options.length > 2 && (
                      <DeleteBtn type="button" onClick={() => removePollOption(idx)} title="Удалить вариант">Удалить</DeleteBtn>
                    )}
                  </div>
                ))}
                <Button type="button" style={{ background: '#ffe082', color: '#232931' }} onClick={addPollOption}>Добавить вариант</Button>
              </div>
              <div
                style={{
                  padding: 10,
                  borderRadius: 10,
                  border: '1px solid #cbd5e1',
                  background: '#f8fafc',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                <div style={{ fontWeight: 700, color: '#334155', fontSize: '0.9em' }}>Фон карточки «Голосование»</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span
                    title="Образец градиента"
                    style={{
                      width: 36,
                      height: 26,
                      borderRadius: 6,
                      border: '1px solid #94a3b8',
                      flexShrink: 0,
                      background: pollForm.cardBackground
                        ? getNewsCardBackgroundCss(pollForm.cardBackground)
                        : 'repeating-conic-gradient(#e2e8f0 0% 25%, #cbd5e1 0% 50%) 50% / 10px 10px',
                    }}
                  />
                  <select
                    value={pollForm.cardBackground}
                    onChange={(e) => setPollForm((p) => ({ ...p, cardBackground: e.target.value }))}
                    aria-label="Градиент фона карточки опроса"
                    style={{
                      flex: 1,
                      minWidth: 160,
                      padding: '6px 8px',
                      borderRadius: 8,
                      border: '1px solid #cbd5e1',
                      fontSize: '0.88em',
                    }}
                  >
                    <option value="">По умолчанию</option>
                    <optgroup label="Светлые">
                      {NEWS_POLL_CARD_BACKGROUND_PRESETS.filter((p) => p.group === 'light').map((p) => (
                        <option key={p.id} value={p.id}>{p.label}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Тёмные">
                      {NEWS_POLL_CARD_BACKGROUND_PRESETS.filter((p) => p.group === 'dark').map((p) => (
                        <option key={p.id} value={p.id}>{p.label}</option>
                      ))}
                    </optgroup>
                  </select>
                </div>
                <div style={{ fontSize: '0.78em', color: '#64748b', lineHeight: 1.4 }}>
                  Фото фона (отдельно телефон и ПК). JPEG, PNG, GIF, WebP, до 8 МБ.
                </div>
                <div>
                  <div style={{ fontWeight: 600, color: '#475569', fontSize: '0.82em', marginBottom: 4 }}>Для телефона</div>
                  <input
                    ref={pollCardImageInputRefMobile}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    disabled={pollCardImageUploadingMobile}
                    onChange={(ev) => handlePollCardImageFile(ev, 'mobile')}
                    style={{ width: '100%', fontSize: '0.85em' }}
                  />
                  {pollCardImageUploadingMobile ? (
                    <div style={{ marginTop: 4, fontSize: '0.78em', color: '#64748b' }}>Загрузка…</div>
                  ) : null}
                  {pollForm.cardImageUrlMobile ? (
                    <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <img
                        src={resolveUploadsUrl(pollForm.cardImageUrlMobile) || pollForm.cardImageUrlMobile}
                        alt=""
                        style={{ maxHeight: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0' }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setPollForm((p) => ({ ...p, cardImageUrlMobile: '' }));
                          if (pollCardImageInputRefMobile.current) pollCardImageInputRefMobile.current.value = '';
                        }}
                        style={{
                          alignSelf: 'flex-start',
                          border: 'none',
                          background: 'transparent',
                          color: '#dc2626',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          fontSize: '0.82em',
                        }}
                      >
                        Убрать (телефон)
                      </button>
                    </div>
                  ) : null}
                </div>
                <div>
                  <div style={{ fontWeight: 600, color: '#475569', fontSize: '0.82em', marginBottom: 4 }}>Для ПК</div>
                  <input
                    ref={pollCardImageInputRefDesktop}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    disabled={pollCardImageUploadingDesktop}
                    onChange={(ev) => handlePollCardImageFile(ev, 'desktop')}
                    style={{ width: '100%', fontSize: '0.85em' }}
                  />
                  {pollCardImageUploadingDesktop ? (
                    <div style={{ marginTop: 4, fontSize: '0.78em', color: '#64748b' }}>Загрузка…</div>
                  ) : null}
                  {pollForm.cardImageUrl ? (
                    <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <img
                        src={resolveUploadsUrl(pollForm.cardImageUrl) || pollForm.cardImageUrl}
                        alt=""
                        style={{ maxHeight: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0' }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setPollForm((p) => ({ ...p, cardImageUrl: '' }));
                          if (pollCardImageInputRefDesktop.current) pollCardImageInputRefDesktop.current.value = '';
                        }}
                        style={{
                          alignSelf: 'flex-start',
                          border: 'none',
                          background: 'transparent',
                          color: '#dc2626',
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          fontSize: '0.82em',
                        }}
                      >
                        Убрать (ПК)
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
              {pollCreateError && <div style={{ color: '#e74c3c' }}>{pollCreateError}</div>}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <Button type="button" style={{ background: '#e0f7fa', color: '#232931' }} onClick={closeCreatePollModal}>Отмена</Button>
                <Button type="button" onClick={submitPoll} disabled={pollCreateLoading} style={{ opacity: pollCreateLoading ? 0.7 : 1 }}>
                  {pollCreateLoading ? 'Создание...' : 'Опубликовать'}
                </Button>
              </div>
            </div>
          </CommentModal>
        </CommentModalBg>
      )}

      <CardList $modalMobile={isModalMobileFeed}>
        {/* Modal: render unified combined feed */}
        {modal && combinedFeed.map(entry => {
          if (entry.kind === 'congrat') {
            const c = entry.item;
            const congratCardStyle = isModalMobileFeed
              ? {
                  border: 'none',
                  borderLeft: 'none',
                  borderRadius: 20,
                  boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
                  background: 'linear-gradient(160deg, rgba(254, 215, 170, 0.22) 0%, rgba(251, 191, 36, 0.12) 45%, rgba(15, 23, 42, 0.35) 100%)',
                  backdropFilter: 'blur(16px)',
                  padding: '18px 16px 16px',
                }
              : { borderLeft: '7px solid #fcb69f', background: 'linear-gradient(135deg, #fff5f2 60%, #ffe8e0 100%)' };
            const congratHeadRow = isModalMobileFeed
              ? { display: 'flex', alignItems: 'flex-start', gap: 12 }
              : { display: 'flex', alignItems: 'center', gap: 10 };
            const congratActions = isModalMobileFeed
              ? {
                  marginTop: 16,
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 10,
                  width: '100%',
                }
              : { marginTop: 12, display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' };
            return (
              <Card key={`congrat-${c.id}`} ref={el => { itemRefs.current[`congrat-${c.id}`] = el; }} style={congratCardStyle}>
                <CardInfo>
                  <div style={congratHeadRow}>
                    {getAuthorAvatarForCongrat(c) || getAvatarUrl(c)
                      ? <img src={getAuthorAvatarForCongrat(c) || getAvatarUrl(c)} alt="avatar" style={{ width: isModalMobileFeed ? 44 : 36, height: isModalMobileFeed ? 44 : 36, borderRadius: isModalMobileFeed ? 14 : 10, objectFit: 'cover', boxShadow: '0 4px 16px rgba(0,0,0,0.25)', flexShrink: 0 }} />
                      : <FaUserCircle size={isModalMobileFeed ? 44 : 32} color={isModalMobileFeed ? '#fdba74' : '#b2bec3'} title="Аватар" style={{ flexShrink: 0 }} />}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {isModalMobileFeed ? (
                        <div style={{ fontWeight: 800, fontSize: '1.12em', color: '#fff7ed', marginBottom: 6, letterSpacing: '0.02em' }}>
                          {c.first_name} {c.last_name}
                        </div>
                      ) : (
                        <CardTitle style={{ display: 'block', marginBottom: 8 }}>
                          {c.first_name} {c.last_name}
                        </CardTitle>
                      )}
                      <span style={{ color: isModalMobileFeed ? 'rgba(255,247,237,0.72)' : '#888', fontSize: '0.92em', marginBottom: 8, display: 'block' }}>
                        {c.birth_day && c.birth_month && c.birth_year
                          ? `Дата рождения: ${c.birth_day.padStart(2, '0')}.${c.birth_month.padStart(2, '0')}.${c.birth_year}`
                          : ''}
                      </span>
                      {c.file_url && (
                        c.file_url.endsWith('.mp4') ? (
                          <video src={c.file_url} controls style={{ maxWidth: '100%', width: isModalMobileFeed ? '100%' : 320, maxHeight: 220, margin: '10px 0', borderRadius: 14, boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }} />
                        ) : (
                          <img src={c.file_url} alt="media" style={{ maxWidth: '100%', width: isModalMobileFeed ? '100%' : 320, maxHeight: 220, margin: '10px 0', borderRadius: 14, boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }} />
                        )
                      )}
                      {isModalMobileFeed ? (
                        <ModalMobileHtmlBodyWarm
                          style={{ display: 'block', marginTop: 8, whiteSpace: 'pre-line', fontSize: '1.02em' }}
                          dangerouslySetInnerHTML={{ __html: safeHtml(c.congrat_text || '') }}
                        />
                      ) : (
                        <CardContent
                          style={{ display: 'block', marginTop: 8, whiteSpace: 'pre-line', fontSize: '1.08em' }}
                          dangerouslySetInnerHTML={{ __html: safeHtml(c.congrat_text || '') }}
                        />
                      )}
                    </div>
                  </div>
                  <div style={congratActions}>
                    <Button
                      style={{
                        background: isModalMobileFeed ? 'rgba(255,255,255,0.14)' : '#e0f7fa',
                        color: isModalMobileFeed ? '#fff' : '#232931',
                        border: isModalMobileFeed ? 'none' : undefined,
                        boxShadow: isModalMobileFeed ? 'none' : undefined,
                        filter: isModalMobileFeed ? 'none' : undefined,
                        borderRadius: isModalMobileFeed ? 14 : undefined,
                        padding: isModalMobileFeed ? '12px 10px' : undefined,
                        fontWeight: 700,
                        opacity: likeLoading[c.id] ? 0.6 : 1,
                        cursor: likeLoading[c.id] ? 'not-allowed' : 'pointer',
                      }}
                      onClick={() => handleLike(c.id, false, c.likedByUser)}
                      disabled={likeLoading[c.id]}
                    >
                      {likeLoading[c.id] ? '⏳' : '👍'} Лайк ({c.likes || 0})
                    </Button>
                    <Button
                      style={{
                        background: isModalMobileFeed ? 'linear-gradient(135deg, #0ea5e9 0%, #06b6d4 100%)' : '#2193b0',
                        color: '#fff',
                        border: 'none',
                        boxShadow: isModalMobileFeed ? '0 4px 16px rgba(14,165,233,0.35)' : undefined,
                        filter: 'none',
                        borderRadius: isModalMobileFeed ? 14 : undefined,
                        padding: isModalMobileFeed ? '12px 10px' : undefined,
                        fontWeight: 700,
                      }}
                      onClick={() => {
                        setCommentModal({ open: true, congratId: c.id, newsId: null });
                        setCommentText('');
                        setShowEmoji(false);
                      }}
                    >
                      💬 Комментировать
                    </Button>
                    {isAdminOrHr && (
                      <DeleteBtn
                        style={{
                          marginLeft: isModalMobileFeed ? 0 : 8,
                          gridColumn: isModalMobileFeed ? '1 / -1' : undefined,
                          borderRadius: isModalMobileFeed ? 14 : undefined,
                          justifySelf: isModalMobileFeed ? 'stretch' : undefined,
                          textAlign: 'center',
                        }}
                        onClick={() => handleDeleteCongrat(c.id)}
                      >
                        Удалить
                      </DeleteBtn>
                    )}
                  </div>
                  {Array.isArray(c.comments) && c.comments.length > 0 && (
                    <CommentsBlock>
                      <strong style={{ color: '#e74c3c', fontSize: '1.08em' }}>Комментарии:</strong>
                      <ul style={{ marginTop: 10, marginBottom: 0, padding: 0 }}>
                        {c.comments.map((cm, i) => (
                          <CommentItem key={i}>
                            {cm && typeof cm === 'object' && cm.avatar
                              ? <img src={cm.avatar} alt="avatar" style={{ width: 28, height: 28, borderRadius: 8, objectFit: 'cover', boxShadow: '0 1px 4px #e74c3c22' }} />
                              : <FaUserCircle size={24} color="#b2bec3" title="Аватар" />}
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                              <span style={{ fontWeight: 600, color: '#e74c3c', marginBottom: 2 }}>{cm && typeof cm === 'object' ? (cm.username || '') : ''}</span>
                              <span style={{ background: '#fff', borderRadius: 6, padding: '6px 12px', fontSize: '1.04em', color: '#222', boxShadow: '0 1px 4px #e74c3c11', fontFamily: "'Segoe UI Emoji', 'Noto Color Emoji', 'Apple Color Emoji', 'Segoe UI', 'Arial', sans-serif" }}>{renderTextWithCustomEmojis(cm && typeof cm === 'object' ? (cm.commentText || cm.text || '') : String(cm), 'congratsComments')}</span>
                            </div>
                          </CommentItem>
                        ))}
                      </ul>
                    </CommentsBlock>
                  )}
                </CardInfo>
              </Card>
            );
          }
          // news entry
          const item = entry.item;
          const isPlanned = item.publishAt && new Date(item.publishAt) > now;
          const isCongrat = !!item.congratulationId || (item.title && item.title.startsWith('Поздравление'));
          const isPoll = item.type === 'poll' || !!item.pollOptions;
          
          if (isPoll) {
            // --- Опрос в ленте новостей с голосованием ---
            const options = item.options || item.pollOptions || [];
            const votes = item.votes || [];
            const voters = item.voters || [];
            const totalVotes = votes.reduce((a, b) => a + b, 0);
            const { hasVoted, optionIdx: userVotedIdx } = getUserPollVoteInfo(voters, userId);

            const info = votersInfo[item.id] || { voted: [], notVoted: [], totalWorkUsers: 0 };

            const cardBgPresetPoll = String(
              newsRowField(item, 'cardBackground', 'cardbackground') || ''
            ).trim();
            const cardBgCssPoll = cardBgPresetPoll ? getNewsCardBackgroundCss(cardBgPresetPoll) : '';
            const cardImageDesktopPoll = String(
              newsRowField(item, 'cardImageUrl', 'cardimageurl') || ''
            ).trim();
            const cardImageMobilePoll = String(
              newsRowField(item, 'cardImageUrlMobile', 'cardimageurlmobile') || ''
            ).trim();
            const preferMobilePoll = isMobileDevice || isModalMobileFeed;
            const pollCardMedia = pickCardBackgroundMedia({
              cardImageDesktopRaw: cardImageDesktopPoll,
              cardImageMobileRaw: cardImageMobilePoll,
              cardVideoDesktopRaw: '',
              cardVideoMobileRaw: '',
              preferMobile: preferMobilePoll,
            });
            const hasPollImageBg = pollCardMedia?.type === 'image';
            const usePollPhotoChrome = hasPollImageBg && !isModalMobileFeed;
            const pollPresetEntry = cardBgPresetPoll
              ? NEWS_POLL_CARD_BACKGROUND_PRESETS.find((p) => p.id === cardBgPresetPoll)
              : null;
            const pollUsesLightChrome = !hasPollImageBg && pollPresetEntry?.group === 'light';
            const pollInheritedColor = hasPollImageBg
              ? '#e5e7eb'
              : pollPresetEntry?.group === 'light'
                ? '#0f172a'
                : '#e5e7eb';
            const pollSecondaryMuted = pollUsesLightChrome ? '#64748b' : '#94a3b8';
            const pollSectionHeadingColor = pollUsesLightChrome
              ? '#334155'
              : isModalMobileFeed
                ? '#cbd5e1'
                : '#e2e8f0';
            const pollFaSortColor = pollUsesLightChrome ? '#ca8a04' : '#facc15';

            const pollCardModalStyleBase = isModalMobileFeed
              ? {
                  border: 'none',
                  borderLeft: 'none',
                  borderRadius: 20,
                  boxShadow: '0 12px 40px rgba(0,0,0,0.45)',
                  background: 'linear-gradient(165deg, rgba(30, 58, 95, 0.55) 0%, rgba(15, 23, 42, 0.96) 55%)',
                  backdropFilter: 'blur(20px)',
                  color: '#e5e7eb',
                  padding: '18px 16px 16px',
                }
              : {
                  borderLeft: '7px solid #3b82f6',
                  background: 'linear-gradient(135deg, #111827 0%, #0f172a 100%)',
                  color: '#e5e7eb',
                  padding: isMobileDevice ? '16px 14px 14px 14px' : '22px 24px 18px 24px',
                };

            let pollCardStyleResolvedBase = cardBgCssPoll
              ? {
                  ...pollCardModalStyleBase,
                  background: cardBgCssPoll,
                  ...(isModalMobileFeed ? { backdropFilter: 'none' } : {}),
                }
              : pollCardModalStyleBase;

            if (cardBgCssPoll && pollPresetEntry?.group === 'light') {
              pollCardStyleResolvedBase = { ...pollCardStyleResolvedBase, color: '#0f172a' };
            }

            const pollCardStyleResolved = (() => {
              if (!pollCardMedia || pollCardMedia.type !== 'image') {
                return {
                  ...pollCardStyleResolvedBase,
                  color: pollInheritedColor,
                };
              }
              const {
                padding: _pad,
                background: _bg,
                backdropFilter: bfFromBase,
                color: _c,
                ...cardRest
              } = pollCardStyleResolvedBase;
              const overlayGradient =
                'linear-gradient(165deg, rgba(15, 23, 42, 0.22) 0%, rgba(15, 23, 42, 0.48) 42%, rgba(15, 23, 42, 0.72) 100%)';
              const url = resolveUploadsUrl(pollCardMedia.raw) || pollCardMedia.raw;
              const bgImage = `${overlayGradient}, url(${JSON.stringify(url)})`;
              return {
                ...cardRest,
                color: '#e5e7eb',
                backgroundColor: 'transparent',
                backgroundImage: bgImage,
                backgroundSize: 'cover, cover',
                backgroundPosition: 'center, center',
                backgroundRepeat: 'no-repeat',
                backdropFilter:
                  modal && hasPollImageBg ? 'none' : isModalMobileFeed ? 'none' : bfFromBase,
                padding: 0,
                alignItems: 'stretch',
              };
            })();

            const pollMetaColor = publicationDateLineColor({
              isCongrat: false,
              isModalMobileFeed,
              usePhotoCardLikeMobileChrome:
                usePollPhotoChrome || (hasPollImageBg && isModalMobileFeed),
              rawContentColor: effectiveNewsContentColorForMeta(item),
            });

            return (
              <Card
                key={item.id}
                ref={(el) => {
                  itemRefs.current[`news-${item.id}`] = el;
                }}
                style={pollCardStyleResolved}
              >
                <CardInfo
                  style={
                    hasPollImageBg
                      ? {
                          position: 'relative',
                          zIndex: 2,
                          flexShrink: 0,
                          padding: isModalMobileFeed
                            ? '18px 16px 16px'
                            : isMobileDevice
                              ? '16px 14px 14px'
                              : '22px 24px 18px 24px',
                          width: '100%',
                          boxSizing: 'border-box',
                        }
                      : undefined
                  }
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: isMobileDevice ? 8 : 10, flexWrap: isMobileDevice ? 'wrap' : 'nowrap' }}>
                    {getAvatarUrl(item)
                      ? <img src={getAvatarUrl(item)} alt="avatar" style={{ width: isMobileDevice ? 32 : 36, height: isMobileDevice ? 32 : 36, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }} />
                      : <FaUserCircle size={isMobileDevice ? 28 : 32} color={pollSecondaryMuted} title="Аватар" style={{ flexShrink: 0 }} />}
                    <CardTitle style={{ 
                      marginBottom: 0, 
                      background: 'transparent', 
                      color: 'inherit', 
                      boxShadow: 'none', 
                      textShadow: 'none', 
                      padding: 0,
                      fontSize: isMobileDevice ? '1.1em' : '1.35em',
                      flex: '1 1 auto',
                      minWidth: 0,
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                    }}>
                      <FaSort style={{ color: pollFaSortColor, fontSize: isMobileDevice ? '1em' : '1.2em', marginRight: 4, flexShrink: 0 }} />
                      <span>Голосование</span>
                    </CardTitle>
                  </div>
                  <PublicationMetaText
                    $metaColor={pollMetaColor}
                    style={{
                      fontSize: isMobileDevice ? '0.9em' : '0.98em',
                      marginBottom: isMobileDevice ? 6 : 8,
                      display: 'block',
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                    }}
                  >
                    {item.publishAt
                      ? `Опубликовано: ${new Date(item.publishAt).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}`
                      : ''}
                  </PublicationMetaText>
                  <div style={{
                    marginBottom: isMobileDevice ? 8 : 10, 
                    fontWeight: 700, 
                    fontSize: isMobileDevice ? '1em' : '1.13em', 
                    color: pollInheritedColor,
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                  }}>{item.question || item.content}</div>
                  {/* Fancy progress visualization */}
                  {Array.isArray(options) && options.map((opt, idx) => {
                    const votesForOption = votes[idx] || 0;
                    const percent = totalVotes > 0 ? Math.round((votesForOption / totalVotes) * 100) : 0;
                    const votedUsers = Array.isArray(info.votedByOption)
                      && info.votedByOption[idx]
                      && Array.isArray(info.votedByOption[idx].users)
                      ? info.votedByOption[idx].users
                      : [];
                    return (
                      <CardOption key={idx} style={{
                        opacity: hasVoted && userVotedIdx !== idx ? 0.7 : 1,
                        border: hasVoted && userVotedIdx === idx ? '2.5px solid #43e97b' : 'none',
                        padding: isMobileDevice ? '12px 14px' : '14px 16px',
                        marginBottom: isMobileDevice ? '10px' : '14px',
                      }}>
                        <div style={{ 
                          fontWeight: 700, 
                          fontSize: isMobileDevice ? '0.95em' : '1.02em', 
                          color: '#344054', 
                          marginBottom: isMobileDevice ? 6 : 8,
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                        }}>{opt}</div>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: isMobileDevice ? 'flex-start' : 'center', 
                          justifyContent: 'space-between', 
                          width: '100%', 
                          gap: isMobileDevice ? 8 : 12,
                          flexWrap: isMobileDevice ? 'wrap' : 'nowrap',
                          flexDirection: isMobileDevice ? 'column' : 'row',
                        }}>
                          <div style={{ 
                            flex: 1, 
                            width: isMobileDevice ? '100%' : 'auto',
                            height: isMobileDevice ? 10 : 8, 
                            borderRadius: 999, 
                            background: '#eef2f6', 
                            overflow: 'hidden',
                            minWidth: isMobileDevice ? 0 : 'auto',
                          }}>
                            <div style={{ width: percent + '%', height: '100%', background: '#3b82f6', transition: 'width .4s' }} />
                          </div>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: isMobileDevice ? 6 : 12,
                            width: isMobileDevice ? '100%' : 'auto',
                            flexWrap: isMobileDevice ? 'wrap' : 'nowrap',
                            justifyContent: isMobileDevice ? 'space-between' : 'flex-end',
                          }}>
                            <span style={{ 
                              color: '#475467', 
                              fontWeight: 700, 
                              minWidth: isMobileDevice ? 0 : 88, 
                              textAlign: 'right',
                              fontSize: isMobileDevice ? '0.9em' : '1em',
                              whiteSpace: 'nowrap',
                            }}>{votesForOption} ({percent}%)</span>
                            <button
                              type="button"
                              style={{ 
                                minWidth: isMobileDevice ? 0 : 92, 
                                padding: isMobileDevice ? '5px 8px' : '6px 10px', 
                                background: '#f8fafc', 
                                color: '#344054', 
                                borderRadius: 8, 
                                border: '1px solid #e5eaf0', 
                                fontWeight: 600, 
                                cursor: 'pointer',
                                fontSize: isMobileDevice ? '0.85em' : '0.95em',
                                flex: isMobileDevice ? '1 1 auto' : 'none',
                              }}
                              onClick={() => toggleVotersCollapse(item.id, idx)}
                            >
                              {collapsedVoters[item.id]?.[idx] ? 'Скрыть' : 'Показать'}
                            </button>
                            {pollVoteLoading[item.id] ? (
                              <span
                                style={{
                                  minWidth: isMobileDevice ? 0 : 110,
                                  padding: isMobileDevice ? '5px 8px' : '6px 10px',
                                  fontSize: isMobileDevice ? '0.85em' : '0.95em',
                                  color: pollSecondaryMuted,
                                  fontWeight: 600,
                                }}
                              >
                                Голос…
                              </span>
                            ) : hasVoted && userVotedIdx === idx ? (
                              <span
                                style={{
                                  minWidth: isMobileDevice ? 0 : 110,
                                  padding: isMobileDevice ? '5px 8px' : '6px 10px',
                                  background: 'rgba(67, 233, 123, 0.2)',
                                  color: '#15803d',
                                  borderRadius: 8,
                                  border: '1px solid rgba(67, 233, 123, 0.5)',
                                  fontWeight: 700,
                                  fontSize: isMobileDevice ? '0.85em' : '0.95em',
                                  flex: isMobileDevice ? '1 1 auto' : 'none',
                                  textAlign: 'center',
                                }}
                              >
                                Ваш выбор
                              </span>
                            ) : (
                              <button
                                type="button"
                                style={{
                                  minWidth: isMobileDevice ? 0 : 110,
                                  padding: isMobileDevice ? '5px 8px' : '6px 10px',
                                  background: '#3b82f6',
                                  color: '#fff',
                                  borderRadius: 8,
                                  border: 'none',
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                  fontSize: isMobileDevice ? '0.85em' : '0.95em',
                                  flex: isMobileDevice ? '1 1 auto' : 'none',
                                }}
                                onClick={() => handlePollVote(item.id, idx)}
                              >
                                {hasVoted ? 'Выбрать этот вариант' : 'Голосовать'}
                              </button>
                            )}
                          </div>
                        </div>
                        {hasVoted && (
                          <div style={{
                            position: 'absolute',
                            left: 0, bottom: 0, height: 4,
                            width: percent + '%',
                            background: '#ffe082',
                            borderRadius: '0 0 10px 10px',
                            transition: 'width 0.5s',
                            zIndex: 0,
                          }} />
                        )}
                        <div style={{ marginTop: isMobileDevice ? 10 : 14, fontSize: isMobileDevice ? '0.95em' : '1.04em', fontWeight: 600 }}>
                          {collapsedVoters[item.id]?.[idx] ? (
                            Array.isArray(votedUsers) && votedUsers.length > 0 ? (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: isMobileDevice ? 6 : 10 }}>
                                {votedUsers.map(u => (
                                  <span key={u.id} style={{
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: isMobileDevice ? 5 : 7,
                                    background: 'linear-gradient(90deg, #43e97b22 0%, #ffe08244 100%)',
                                    borderRadius: 50,
                                    padding: isMobileDevice ? '4px 12px' : '5px 16px',
                                    boxShadow: '0 2px 12px #43e97b22',
                                    transition: 'box-shadow 0.2s, transform 0.2s',
                                    fontWeight: 700,
                                    fontSize: isMobileDevice ? '0.9em' : '1.08em',
                                    color: '#2193b0',
                                    letterSpacing: '0.5px',
                                    cursor: 'pointer',
                                    border: '2px solid #43e97b33',
                                    maxWidth: isMobileDevice ? '100%' : 'none',
                                    wordBreak: 'break-word',
                                    overflowWrap: 'break-word',
                                  }}>
                                    {u.avatar ? <img src={u.avatar} alt="avatar" style={{ 
                                      width: isMobileDevice ? 24 : 28, 
                                      height: isMobileDevice ? 24 : 28, 
                                      borderRadius: '50%', 
                                      objectFit: 'cover', 
                                      boxShadow: '0 2px 8px #ffe08233', 
                                      marginRight: isMobileDevice ? 3 : 4,
                                      flexShrink: 0,
                                    }} /> : <FaUserCircle size={isMobileDevice ? 20 : 24} color="#b2bec3" />}
                                    <span style={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      maxWidth: isMobileDevice ? '120px' : 'none',
                                      wordBreak: 'break-word',
                                      overflowWrap: 'break-word',
                                    }}>{u.username}</span>
                                  </span>
                                ))}
                              </div>
                            ) : <span style={{ color: pollSecondaryMuted, fontStyle: 'italic', fontSize: isMobileDevice ? '0.9em' : '1.08em' }}>😶 Нет проголосовавших</span>
                          ) : null}
                        </div>
                      </CardOption>
                    );
                  })}
                  {/* Сообщение об ошибке голосования */}
                  {pollVoteError[item.id] && <div style={{ 
                    color: '#e74c3c', 
                    marginTop: isMobileDevice ? 6 : 8,
                    fontSize: isMobileDevice ? '0.9em' : '0.95em',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                  }}>{pollVoteError[item.id]}</div>}
                  {/* Информация о количестве проголосовавших */}
                  <div style={{ 
                    marginTop: isMobileDevice ? 6 : 8, 
                    color: pollSecondaryMuted, 
                    fontSize: isMobileDevice ? '0.9em' : '0.98em',
                    wordBreak: 'break-word',
                  }}>
                    Проголосовало: {totalVotes}
                  </div>
                  {hasVoted && <div style={{ 
                    color: '#43e97b', 
                    marginTop: isMobileDevice ? 4 : 4, 
                    fontWeight: 600,
                    fontSize: isMobileDevice ? '0.9em' : '0.95em',
                  }}>Вы проголосовали. Можно изменить выбор — кнопка «Выбрать этот вариант» у другого ответа.</div>}

                  {/* Новый блок: кто проголосовал и кто нет */}
                  <div style={{ marginTop: isMobileDevice ? 10 : 14 }}>
                    <div style={{ 
                      fontWeight: 700, 
                      color: pollSectionHeadingColor, 
                      marginBottom: isMobileDevice ? 6 : 4,
                      fontSize: isMobileDevice ? '0.95em' : '1em',
                      wordBreak: 'break-word',
                    }}>Сотрудники, не проголосовали:</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: isMobileDevice ? 6 : 8 }}>
                      {Array.isArray(info.notVoted) && info.notVoted.length === 0 && <span style={{ 
                        color: pollSecondaryMuted,
                        fontSize: isMobileDevice ? '0.9em' : '0.95em',
                      }}>Нет</span>}
                      {Array.isArray(info.notVoted) ? info.notVoted.map(u => (
                        <span key={u.id} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: isMobileDevice ? 3 : 4, 
                          background: '#ffe08222', 
                          borderRadius: 6, 
                          padding: isMobileDevice ? '2px 6px' : '2px 8px',
                          fontSize: isMobileDevice ? '0.85em' : '0.9em',
                          maxWidth: '100%',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                        }}>
                          {u.avatar ? <img src={u.avatar} alt="avatar" style={{ 
                            width: isMobileDevice ? 18 : 22, 
                            height: isMobileDevice ? 18 : 22, 
                            borderRadius: 6, 
                            objectFit: 'cover', 
                            marginRight: isMobileDevice ? 3 : 4,
                            flexShrink: 0,
                          }} /> : <FaUserCircle size={isMobileDevice ? 18 : 20} color="#b2bec3" style={{ flexShrink: 0 }} />}
                          <span style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: isMobileDevice ? '150px' : 'none',
                          }}>{u.username}</span>
                        </span>
                      )) : null}
                    </div>
                  </div>

                  {isAdminOrHr && (
                    <div
                      style={{
                        marginTop: isModalMobileFeed ? 16 : isMobileDevice ? 10 : 14,
                        display: 'flex',
                        justifyContent: isModalMobileFeed ? 'stretch' : 'flex-end',
                      }}
                    >
                      <DeleteBtn
                        style={{
                          marginLeft: 0,
                          width: isModalMobileFeed ? '100%' : undefined,
                          justifyContent: 'center',
                          padding: isModalMobileFeed ? '12px 16px' : isMobileDevice ? '6px 12px' : '7px 14px',
                          fontSize: isModalMobileFeed ? '0.95em' : isMobileDevice ? '0.9em' : '0.93em',
                          borderRadius: isModalMobileFeed ? 14 : undefined,
                        }}
                        onClick={() => handleDeleteNews(item.id)}
                      >
                        🗑️ Удалить опрос
                      </DeleteBtn>
                    </div>
                  )}
                </CardInfo>
              </Card>
            );
          }
          
          // Обычная новость/поздравление
          const newsCardModalStyle = isModalMobileFeed
            ? isCongrat
              ? {
                  border: 'none',
                  borderLeft: 'none',
                  borderRadius: 20,
                  boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
                  background: 'linear-gradient(160deg, rgba(254, 215, 170, 0.2) 0%, rgba(251, 113, 133, 0.1) 50%, rgba(15, 23, 42, 0.4) 100%)',
                  backdropFilter: 'blur(16px)',
                  padding: '18px 16px 16px',
                }
              : {
                  border: 'none',
                  borderLeft: 'none',
                  borderRadius: 20,
                  boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
                  background: 'linear-gradient(165deg, rgba(51, 65, 85, 0.75) 0%, rgba(15, 23, 42, 0.92) 100%)',
                  backdropFilter: 'blur(20px)',
                  padding: '18px 16px 16px',
                }
            : isCongrat
              ? { borderLeft: '7px solid #fcb69f', background: 'linear-gradient(135deg, #fff5f2 60%, #ffe8e0 100%)' }
              : { borderLeft: '7px solid #2193b0', background: 'linear-gradient(135deg, #f5f7ff 60%, #e8f0ff 100%)' };

          const cardBgPreset = !isCongrat
            ? String(newsRowField(item, 'cardBackground', 'cardbackground') || '').trim()
            : '';
          const cardBgCss = cardBgPreset ? getNewsCardBackgroundCss(cardBgPreset) : '';
          const newsCardStyleResolvedBase =
            cardBgCss && !isCongrat
              ? {
                  ...newsCardModalStyle,
                  background: cardBgCss,
                  ...(isModalMobileFeed ? { backdropFilter: 'none' } : {}),
                }
              : newsCardModalStyle;

          const cardImageDesktopRaw = !isCongrat
            ? String(newsRowField(item, 'cardImageUrl', 'cardimageurl') || '').trim()
            : '';
          const cardImageMobileRaw = !isCongrat
            ? String(newsRowField(item, 'cardImageUrlMobile', 'cardimageurlmobile') || '').trim()
            : '';
          const cardVideoDesktopRaw = !isCongrat
            ? String(newsRowField(item, 'cardVideoUrl', 'cardvideourl') || '').trim()
            : '';
          const cardVideoMobileRaw = !isCongrat
            ? String(newsRowField(item, 'cardVideoUrlMobile', 'cardvideourlmobile') || '').trim()
            : '';
          const preferMobileCardMedia = isMobileDevice || isModalMobileFeed;
          const cardMedia = !isCongrat
            ? pickCardBackgroundMedia({
                cardImageDesktopRaw,
                cardImageMobileRaw,
                cardVideoDesktopRaw,
                cardVideoMobileRaw,
                preferMobile: preferMobileCardMedia,
              })
            : null;
          const hasCardMediaBg = Boolean(cardMedia);
          const hasUserFacingText = !isCongrat && newsItemHasUserFacingText(item);
          const useMediaOverlayGradient =
            cardMedia?.type === 'image' ||
            (cardMedia?.type === 'video' && hasUserFacingText);
          const cardImageDisplayUrl =
            cardMedia?.type === 'image' ? resolveUploadsUrl(cardMedia.raw) : '';
          const cardVideoDisplayUrl =
            cardMedia?.type === 'video' ? resolveUploadsUrl(cardMedia.raw) : '';
          /** Десктопная лента: фото/видео фон — те же светлые стили текста, что в мобильной модалке. */
          const usePhotoCardLikeMobileChrome = hasCardMediaBg && !isCongrat && !isModalMobileFeed;
          const isStackedVideoCard =
            cardMedia?.type === 'video' && Boolean(cardVideoDisplayUrl);
          /** На телефоне карточка с видео — высотой экрана; видео сверху с нативными controls, текст под ним. */
          const isPhoneVideoCard =
            isStackedVideoCard && (isModalMobileFeed || isMobileDevice);
          const newsCardStyleResolved = (() => {
            if (!cardMedia) return newsCardStyleResolvedBase;
            const { background: _bgDrop, backdropFilter: bfFromBase, ...cardRest } = newsCardStyleResolvedBase;
            const overlayGradient =
              'linear-gradient(165deg, rgba(15, 23, 42, 0.22) 0%, rgba(15, 23, 42, 0.48) 42%, rgba(15, 23, 42, 0.72) 100%)';
            if (cardMedia.type === 'image') {
              const url = cardImageDisplayUrl || cardMedia.raw;
              const bgImage = useMediaOverlayGradient
                ? `${overlayGradient}, url(${JSON.stringify(url)})`
                : `url(${JSON.stringify(url)})`;
              return {
                ...cardRest,
                backgroundColor: 'transparent',
                backgroundImage: bgImage,
                backgroundSize: useMediaOverlayGradient ? 'cover, cover' : 'cover',
                backgroundPosition: useMediaOverlayGradient ? 'center, center' : 'center',
                backgroundRepeat: 'no-repeat',
                backdropFilter: modal && hasCardMediaBg ? 'none' : isModalMobileFeed ? 'none' : bfFromBase,
                padding: 0,
                alignItems: 'stretch',
              };
            }
            return {
              ...cardRest,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              position: 'relative',
              overflow: 'hidden',
              backgroundColor: '#0f172a',
              backdropFilter: modal && hasCardMediaBg ? 'none' : isModalMobileFeed ? 'none' : bfFromBase,
              padding: 0,
              width: '100%',
              boxSizing: 'border-box',
              ...(isPhoneVideoCard
                ? { minHeight: '100dvh' }
                : hasUserFacingText
                  ? {}
                  : { minHeight: 400 }),
            };
          })();

          const newsHeadRow = isModalMobileFeed
            ? { display: 'flex', alignItems: 'flex-start', gap: 12 }
            : { display: 'flex', alignItems: 'center', gap: 10 };

          const newsActionsStyle = isModalMobileFeed
            ? {
                marginTop: 16,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 10,
                width: '100%',
              }
            : { marginTop: 12, display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' };

          const titleFam = newsRowField(item, 'titleFontFamily', 'titlefontfamily');
          const newsTitleFontStyle =
            !isCongrat && titleFam && String(titleFam).trim()
              ? { fontFamily: titleFam }
              : {};
          const newsTitleExtraStyle = {};
          if (!isCongrat) {
            const tc = newsRowField(item, 'titleColor', 'titlecolor');
            if (tc && String(tc).trim()) {
              newsTitleExtraStyle.color = String(tc).trim();
            }
            const tsz = newsRowField(item, 'titleFontSize', 'titlefontsize');
            if (tsz && String(tsz).trim()) {
              newsTitleExtraStyle.fontSize = String(tsz).trim();
            }
            const tw = newsRowField(item, 'titleFontWeight', 'titlefontweight');
            if (tw && String(tw).trim()) {
              newsTitleExtraStyle.fontWeight = String(tw).trim();
            }
          }
          const newsContentExtraStyle = {};
          if (!isCongrat) {
            const cc = newsRowField(item, 'contentColor', 'contentcolor', 'content_color');
            if (cc && String(cc).trim()) {
              newsContentExtraStyle.color = String(cc).trim();
            }
            const csz = newsRowField(item, 'contentFontSize', 'contentfontsize');
            if (csz && String(csz).trim()) {
              newsContentExtraStyle.fontSize = String(csz).trim();
            }
            const cw = newsRowField(item, 'contentFontWeight', 'contentfontweight');
            if (cw && String(cw).trim()) {
              newsContentExtraStyle.fontWeight = String(cw).trim();
            }
          }
          const hasNewsContentCustomColor = Boolean(newsContentExtraStyle.color);
          const rawContentColorForMeta = !isCongrat ? effectiveNewsContentColorForMeta(item) : '';
          const publicationMetaColor = publicationDateLineColor({
            isCongrat,
            isModalMobileFeed,
            usePhotoCardLikeMobileChrome,
            rawContentColor: rawContentColorForMeta,
          });
          const newsBodyFontFamily = newsContentFontStack(item);
          const newsContentBgRaw = !isCongrat
            ? String(newsRowField(item, 'contentBackground', 'contentbackground') || '').trim()
            : '';
          const newsContentBgCss = newsContentBgRaw ? getNewsContentBackgroundCss(newsContentBgRaw) : '';
          const newsContentSurfaceWrap = newsContentBgCss
            ? {
                marginTop: 8,
                padding: '12px 14px',
                borderRadius: 12,
                background: newsContentBgCss,
                border:
                  isModalMobileFeed || usePhotoCardLikeMobileChrome
                    ? '1px solid rgba(255,255,255,0.14)'
                    : '1px solid rgba(148,163,184,0.35)',
              }
            : null;

          const newsActionsGlassChrome = isModalMobileFeed || usePhotoCardLikeMobileChrome;

          const stackedVideoAreaStyle = {
            flex: isPhoneVideoCard ? '1 1 0%' : '0 0 auto',
            minHeight: 0,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            background: '#000',
          };
          const stackedVideoElStyle = {
            width: '100%',
            flex: isPhoneVideoCard ? 1 : 'none',
            minHeight: isPhoneVideoCard ? 'min(52dvh, 55vh)' : 280,
            height: isPhoneVideoCard ? undefined : 340,
            maxHeight: isPhoneVideoCard ? 'none' : 520,
            objectFit: 'cover',
            display: 'block',
          };

          return (
            <Card key={`news-${item.id}`} ref={el => { itemRefs.current[`news-${item.id}`] = el; }} style={newsCardStyleResolved}>
              {isStackedVideoCard ? (
                <div style={stackedVideoAreaStyle}>
                  <video
                    key={cardVideoDisplayUrl}
                    src={cardVideoDisplayUrl}
                    controls
                    playsInline
                    preload="metadata"
                    muted
                    autoPlay
                    loop
                    style={stackedVideoElStyle}
                  />
                </div>
              ) : null}
              <CardInfo
                style={
                  hasCardMediaBg
                    ? {
                        position: 'relative',
                        zIndex: 2,
                        flexShrink: 0,
                        padding: isModalMobileFeed ? '18px 16px 16px' : '22px 24px 18px 24px',
                        width: '100%',
                        boxSizing: 'border-box',
                        ...(isStackedVideoCard
                          ? {
                              background:
                                'linear-gradient(180deg, rgba(15,23,42,0.2) 0%, rgba(15,23,42,0.94) 12%, rgba(15,23,42,0.98) 100%)',
                              borderTop: '1px solid rgba(255,255,255,0.08)',
                            }
                          : {}),
                      }
                    : undefined
                }
              >
                <div style={newsHeadRow}>
                  {getAvatarUrl(item)
                    ? <img src={getAvatarUrl(item)} alt="avatar" style={{ width: isModalMobileFeed ? 44 : 36, height: isModalMobileFeed ? 44 : 36, borderRadius: isModalMobileFeed ? 14 : 10, objectFit: 'cover', boxShadow: isModalMobileFeed ? '0 4px 16px rgba(0,0,0,0.3)' : '0 2px 8px #e74c3c33', flexShrink: 0 }} />
                    : <FaUserCircle size={isModalMobileFeed ? 44 : 32} color={isModalMobileFeed ? (isCongrat ? '#fdba74' : '#94a3b8') : usePhotoCardLikeMobileChrome ? '#94a3b8' : '#b2bec3'} title="Аватар" style={{ flexShrink: 0 }} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {isModalMobileFeed || usePhotoCardLikeMobileChrome ? (
                      <div
                        style={{
                          fontWeight: usePhotoCardLikeMobileChrome ? 900 : 800,
                          fontSize: usePhotoCardLikeMobileChrome ? '1.35em' : '1.1em',
                          color: isCongrat ? '#fff7ed' : '#f1f5f9',
                          marginBottom: 6,
                          letterSpacing: '0.02em',
                          wordBreak: 'break-word',
                          ...(usePhotoCardLikeMobileChrome
                            ? { textShadow: '0 2px 14px rgba(0,0,0,0.75)' }
                            : {}),
                          ...newsTitleFontStyle,
                          ...newsTitleExtraStyle,
                        }}
                      >
                        {item.title}
                      </div>
                    ) : (
                      <CardTitle style={{ display: 'block', marginBottom: 8, ...newsTitleFontStyle, ...newsTitleExtraStyle }}>
                        {item.title}
                      </CardTitle>
                    )}
                    <PublicationMetaText
                      $metaColor={publicationMetaColor}
                      style={{ fontSize: '0.92em', marginBottom: 8, display: 'block' }}
                    >
                      {isPlanned && isAdminOrHr
                        ? `Запланировано на: ${new Date(item.publishAt).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}`
                        : item.publishAt
                          ? `Опубликовано: ${new Date(item.publishAt).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}`
                          : ''}
                    </PublicationMetaText>
                    {isCongrat && item.file_url && (
                      item.file_url.endsWith('.mp4') ? (
                        <video src={item.file_url} controls style={{ maxWidth: '100%', width: isModalMobileFeed ? '100%' : 320, maxHeight: 220, margin: '10px 0', borderRadius: 14, boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }} />
                      ) : (
                        <img src={item.file_url} alt="media" style={{ maxWidth: '100%', width: isModalMobileFeed ? '100%' : 320, maxHeight: 220, margin: '10px 0', borderRadius: 14, boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }} />
                      )
                    )}
                    {isModalMobileFeed ? (
                      isCongrat ? (
                        <ModalMobileHtmlBodyWarm
                          style={{ display: 'block', marginTop: 8, fontSize: '1.02em', lineHeight: 1.55, wordBreak: 'break-word', fontFamily: newsBodyFontFamily }}
                          dangerouslySetInnerHTML={{ __html: safeHtml(item.content || '') }}
                        />
                      ) : newsContentSurfaceWrap ? (
                        <div style={newsContentSurfaceWrap}>
                          <ModalMobileHtmlBody
                            $customContentColor={hasNewsContentCustomColor}
                            style={{
                              display: 'block',
                              marginTop: 0,
                              fontSize: '1.02em',
                              lineHeight: 1.55,
                              wordBreak: 'break-word',
                              fontFamily: newsBodyFontFamily,
                              ...newsContentExtraStyle,
                            }}
                            dangerouslySetInnerHTML={{ __html: safeHtml(item.content || '') }}
                          />
                        </div>
                      ) : (
                        <ModalMobileHtmlBody
                          $customContentColor={hasNewsContentCustomColor}
                          style={{
                            display: 'block',
                            marginTop: 8,
                            fontSize: '1.02em',
                            lineHeight: 1.55,
                            wordBreak: 'break-word',
                            fontFamily: newsBodyFontFamily,
                            ...newsContentExtraStyle,
                          }}
                          dangerouslySetInnerHTML={{ __html: safeHtml(item.content || '') }}
                        />
                      )
                    ) : usePhotoCardLikeMobileChrome ? (
                      newsContentSurfaceWrap ? (
                        <div style={newsContentSurfaceWrap}>
                          <ModalMobileHtmlBody
                            $customContentColor={hasNewsContentCustomColor}
                            style={{
                              display: 'block',
                              marginTop: 0,
                              fontSize: '1.08em',
                              lineHeight: 1.55,
                              wordBreak: 'break-word',
                              fontFamily: newsBodyFontFamily,
                              ...newsContentExtraStyle,
                            }}
                            dangerouslySetInnerHTML={{ __html: safeHtml(item.content || '') }}
                          />
                        </div>
                      ) : (
                        <ModalMobileHtmlBody
                          $customContentColor={hasNewsContentCustomColor}
                          style={{
                            display: 'block',
                            marginTop: 8,
                            fontSize: '1.08em',
                            lineHeight: 1.55,
                            wordBreak: 'break-word',
                            fontFamily: newsBodyFontFamily,
                            ...newsContentExtraStyle,
                          }}
                          dangerouslySetInnerHTML={{ __html: safeHtml(item.content || '') }}
                        />
                      )
                    ) : newsContentSurfaceWrap ? (
                      <div style={newsContentSurfaceWrap}>
                        <CardContent
                          $customContentColor={hasNewsContentCustomColor}
                          style={{
                            display: 'block',
                            marginTop: 0,
                            fontSize: '1.08em',
                            lineHeight: 1.5,
                            wordBreak: 'break-word',
                            fontFamily: newsBodyFontFamily,
                            ...newsContentExtraStyle,
                          }}
                          dangerouslySetInnerHTML={{ __html: safeHtml(item.content || '') }}
                        />
                      </div>
                    ) : (
                      <CardContent
                        $customContentColor={hasNewsContentCustomColor}
                        style={{
                          display: 'block',
                          marginTop: 8,
                          fontSize: '1.08em',
                          lineHeight: 1.5,
                          wordBreak: 'break-word',
                          fontFamily: newsBodyFontFamily,
                          ...newsContentExtraStyle,
                        }}
                        dangerouslySetInnerHTML={{ __html: safeHtml(item.content || '') }}
                      />
                    )}
                  </div>
                </div>
                <div style={newsActionsStyle}>
                  <Button
                    style={{
                      background: newsActionsGlassChrome
                        ? isCongrat
                          ? 'rgba(255,255,255,0.14)'
                          : 'rgba(255,255,255,0.1)'
                        : isCongrat
                          ? '#e0f7fa'
                          : '#2193b0',
                      color: newsActionsGlassChrome ? '#fff' : isCongrat ? '#232931' : '#fff',
                      border: newsActionsGlassChrome ? 'none' : undefined,
                      boxShadow: newsActionsGlassChrome ? 'none' : undefined,
                      filter: newsActionsGlassChrome ? 'none' : undefined,
                      borderRadius: newsActionsGlassChrome ? 14 : undefined,
                      padding: newsActionsGlassChrome ? '12px 10px' : undefined,
                      fontWeight: 700,
                      opacity: likeLoading[isCongrat ? item.congratulationId : item.id] ? 0.6 : 1,
                      cursor: likeLoading[isCongrat ? item.congratulationId : item.id] ? 'not-allowed' : 'pointer',
                    }}
                    onClick={() => handleLike(isCongrat ? item.congratulationId : item.id, !isCongrat, isCongrat ? item.likedByUser : item.newsLikes?.some(like => like.user_id === userId))}
                    disabled={likeLoading[isCongrat ? item.congratulationId : item.id]}
                  >
                    {likeLoading[isCongrat ? item.congratulationId : item.id] ? '⏳' : '👍'} Лайк ({(isCongrat ? item.likes : (item.newsLikes?.length || 0)) || 0})
                  </Button>
                  <Button
                    style={{
                      background: newsActionsGlassChrome ? 'linear-gradient(135deg, #0ea5e9 0%, #06b6d4 100%)' : '#2193b0',
                      color: '#fff',
                      border: 'none',
                      boxShadow: newsActionsGlassChrome ? '0 4px 16px rgba(14,165,233,0.35)' : undefined,
                      filter: 'none',
                      borderRadius: newsActionsGlassChrome ? 14 : undefined,
                      padding: newsActionsGlassChrome ? '12px 10px' : undefined,
                      fontWeight: 700,
                    }}
                    onClick={() => {
                      setCommentModal({ open: true, congratId: isCongrat ? item.congratulationId : null, newsId: !isCongrat ? item.id : null });
                      setCommentText('');
                      setShowEmoji(false);
                    }}
                  >
                    💬 Комментировать
                  </Button>
                  {isAdminOrHr && (
                    <DeleteBtn
                      style={{
                        marginLeft: isModalMobileFeed ? 0 : !isCongrat ? 8 : 0,
                        gridColumn: isModalMobileFeed ? '1 / -1' : undefined,
                        borderRadius: isModalMobileFeed ? 14 : undefined,
                        justifySelf: isModalMobileFeed ? 'stretch' : undefined,
                        textAlign: 'center',
                      }}
                      onClick={() => {
                        if (isCongrat && item.congratulationId) {
                          handleDeleteCongrat(item.congratulationId);
                        } else {
                          handleDeleteNews(item.id);
                        }
                      }}
                    >
                      Удалить
                    </DeleteBtn>
                  )}
                </div>
                {(() => {
                  const fromCongrat = Array.isArray(item.comments) ? item.comments : [];
                  const fromNews = Array.isArray(item.newsComments) ? item.newsComments : [];
                  const merged = [
                    ...fromCongrat.map((c, i) => ({ c, i, area: 'congratsComments', key: `cc-${i}` })),
                    ...fromNews.map((c, i) => ({ c, i, area: 'newsComments', key: c.id != null ? `nc-${c.id}` : `nc-i-${i}` })),
                  ];
                  if (merged.length === 0) return null;
                  return (
                  <CommentsBlock>
                    <strong style={{ color: '#e74c3c', fontSize: '1.08em' }}>Комментарии:</strong>
                    <ul style={{ marginTop: 10, marginBottom: 0, padding: 0 }}>
                      {merged.map(({ c: cmt, area, key }) => (
                        <CommentItem key={key}>
                          {cmt.avatar
                            ? <img src={cmt.avatar} alt="avatar" style={{ width: 28, height: 28, borderRadius: 8, objectFit: 'cover', boxShadow: '0 1px 4px #e74c3c22' }} />
                            : <FaUserCircle size={24} color="#b2bec3" title="Аватар" />}
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontWeight: 600, color: '#e74c3c', marginBottom: 2 }}>{cmt.username}</span>
                            <span style={{ background: '#fff', borderRadius: 6, padding: '6px 12px', fontSize: '1.04em', color: '#222', boxShadow: '0 1px 4px #e74c3c11', fontFamily: "'Segoe UI Emoji', 'Noto Color Emoji', 'Apple Color Emoji', 'Segoe UI', 'Arial', sans-serif" }}>{renderTextWithCustomEmojis(cmt.commentText || cmt.text, area)}</span>
                          </div>
                        </CommentItem>
                      ))}
                    </ul>
                  </CommentsBlock>
                  );
                })()}
              </CardInfo>
            </Card>
          );
        })}

        {/* Non-modal: original split rendering */}
        {!modal && [...congratulations].sort((a, b) => b.id - a.id).map(congrat => (
          <Card key={congrat.id} style={{ borderLeft: '7px solid #fcb69f' }}>
            <CardInfo>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {getAuthorAvatarForCongrat(congrat) || getAvatarUrl(congrat)
                  ? <img src={getAuthorAvatarForCongrat(congrat) || getAvatarUrl(congrat)} alt="avatar" style={{ width: 36, height: 36, borderRadius: 10, objectFit: 'cover', boxShadow: '0 2px 8px #e74c3c33' }} />
                  : <FaUserCircle size={32} color="#b2bec3" title="Аватар" />}
                <div>
                  <CardTitle style={{ display: 'block', marginBottom: 8 }}>
                    {congrat.first_name} {congrat.last_name}
                  </CardTitle>
                  <span style={{ color: '#888', fontSize: '0.98em', marginBottom: 8, display: 'block' }}>
                    {congrat.birth_day && congrat.birth_month && congrat.birth_year
                      ? `Дата рождения: ${congrat.birth_day.padStart(2, '0')}.${congrat.birth_month.padStart(2, '0')}.${congrat.birth_year}`
                      : ''}
                  </span>
                  {congrat.file_url && (
                    congrat.file_url.endsWith('.mp4') ? (
                      <video src={congrat.file_url} controls style={{ maxWidth: 320, maxHeight: 220, margin: '10px 0', borderRadius: 12, boxShadow: '0 2px 8px #2193b033' }} />
                    ) : (
                      <img src={congrat.file_url} alt="media" style={{ maxWidth: 320, maxHeight: 220, margin: '10px 0', borderRadius: 12, boxShadow: '0 2px 8px #2193b033' }} />
                    )
                  )}
                  <CardContent 
                    style={{ display: 'block', marginTop: 8, whiteSpace: 'pre-line', fontSize: '1.08em' }}
                    dangerouslySetInnerHTML={{ __html: safeHtml(congrat.congrat_text || '') }}
                  />
                </div>
              </div>
              <div style={{ marginTop: 12, display: 'flex', gap: 14, alignItems: 'center' }}>
                <Button style={{ background: '#e0f7fa', color: '#232931' }} onClick={() => handleLike(congrat.id, false, congrat.likedByUser)}>👍 Лайк ({congrat.likes || 0})</Button>
                <Button style={{ background: '#2193b0', color: '#fff' }} onClick={() => {
                  setCommentModal({ open: true, congratId: congrat.id, newsId: null });
                  setCommentText('');
                  setShowEmoji(false);
                }}>💬 Комментировать</Button>
                {isAdminOrHr && (
                  <DeleteBtn style={{ marginLeft: 8 }} onClick={() => handleDeleteCongrat(congrat.id)}>Удалить</DeleteBtn>
                )}
              </div>
            </CardInfo>
          </Card>
        ))}

        {/* Non-modal news rendering continues above; remove stray opener */}
      </CardList>

      {/* Comment modal */}
      {commentModal.open && (
        <CommentModalBg onClick={() => setCommentModal({ open: false, congratId: null, newsId: null })}>
          <CommentModal onClick={e => e.stopPropagation()}>
            {/* Close */}
            <button onClick={() => setCommentModal({ open: false, congratId: null, newsId: null })} style={{ position: 'absolute', top: 12, right: 16, fontSize: 24, background: 'none', border: 'none', cursor: 'pointer', color: '#2193b0' }}>×</button>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              {currentCommentTarget?.kind === 'congrat' ? (
                currentCommentTarget?.data?.avatar_url
                  ? <img src={currentCommentTarget.data.avatar_url} alt="avatar" style={{ width: 36, height: 36, borderRadius: 10, objectFit: 'cover', boxShadow: '0 2px 8px #e74c3c33' }} />
                  : <FaUserCircle size={32} color="#b2bec3" title="Аватар" />
              ) : (
                currentCommentTarget?.data?.authorAvatar
                  ? <img src={currentCommentTarget.data.authorAvatar} alt="avatar" style={{ width: 36, height: 36, borderRadius: 10, objectFit: 'cover', boxShadow: '0 2px 8px #e74c3c33' }} />
                  : <FaUserCircle size={32} color="#b2bec3" title="Аватар" />
              )}
              <div>
                <div style={{ fontWeight: 800, fontSize: '1.12em', color: '#e74c3c' }}>
                  {currentCommentTarget?.kind === 'congrat' ? 'Комментарий к поздравлению' : 'Комментарий к новости'}
                </div>
                <div style={{ color: '#636e72', fontSize: '0.92em', maxWidth: 540, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {currentCommentTarget?.kind === 'congrat'
                    ? stripHtml(currentCommentTarget?.data?.congrat_text || '')
                    : stripHtml(currentCommentTarget?.data?.title || currentCommentTarget?.data?.content || '')}
                </div>
              </div>
            </div>

            {/* Toolbar */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => setShowEmoji(v => !v)}
                  style={{ background: 'linear-gradient(135deg, #ffe082 0%, #fcb69f 100%)', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', boxShadow: '0 1px 6px #e74c3c33', color: '#232931', fontWeight: 700 }}
                  title="Эмодзи"
                >
                  😊 Эмодзи
                </button>
              </div>
              <div style={{ color: commentText.length > MAX_COMMENT_LEN ? '#e74c3c' : '#888', fontWeight: 700 }}>
                {commentText.length}/{MAX_COMMENT_LEN}
              </div>
            </div>

            {/* Input */}
            <div style={{ position: 'relative', marginBottom: 12, width: '100%' }}>
              <div
                ref={commentEditorRef}
                contentEditable
                role="textbox"
                aria-multiline="true"
                dir="ltr"
                placeholder="Введите текст... (Ctrl/⌘ + Enter — отправить)"
                onInput={(e) => {
                  const text = tokenizeFromHtml(e.currentTarget.innerHTML);
                  if (text.length > MAX_COMMENT_LEN) {
                    // enforce limit by trimming and re-hydrating
                    const trimmed = text.slice(0, MAX_COMMENT_LEN);
                    setCommentText(trimmed);
                  } else {
                    setCommentText(text);
                  }
                }}
                onKeyDown={e => {
                  if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && !isCommentSending) {
                    const text = tokenizeFromHtml(commentEditorRef.current?.innerHTML || '').trim();
                    if (!text) return;
                    if (commentModal.congratId) handleComment(commentModal.congratId, text, false);
                    if (commentModal.newsId) handleComment(commentModal.newsId, text, true);
                    // clear editor on send
                    if (commentEditorRef.current) commentEditorRef.current.innerHTML = '';
                  }
                }}
                style={{
                  fontSize: '1.18em',
                  minHeight: 100,
                  paddingRight: 48,
                  fontFamily: "'Segoe UI Emoji', 'Noto Color Emoji', 'Apple Color Emoji', 'Segoe UI', 'Arial', sans-serif",
                  border: '1.5px solid #2193b0',
                  borderRadius: 10,
                  padding: '12px 14px',
                  background: '#f9f9f9',
                  outline: 'none'
                }}
              />
              {/* Removed live preview to avoid duplicate text while typing */}
              {showEmoji && (
                <div style={{ position: 'absolute', left: 0, right: 0, top: '100%', marginTop: 8, zIndex: 100 }} onClick={e => e.stopPropagation()}>
                  <CustomEmojiPicker
                    isOpen={showEmoji}
                    onClose={() => setShowEmoji(false)}
                    stackZIndexBase={modal ? 230000 : undefined}
                    onEmojiSelect={(emoji) => {
                      const token = String(emoji || '');
                      if (token.startsWith('custom:')) insertCustomEmojiAtCursor(token);
                      else insertPlainTextInCommentEditor(token);
                    }}
                  />
                </div>
              )}
            </div>

            {commentError && <div style={{ color: '#e74c3c', marginBottom: 8 }}>{commentError}</div>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <Button
                type="button"
                style={{ background: '#e0f7fa', color: '#232931' }}
                onClick={() => setCommentModal({ open: false, congratId: null, newsId: null })}
              >Отмена</Button>
              <Button
                type="button"
                style={{ marginTop: 0, opacity: isCommentSending ? 0.6 : 1, pointerEvents: isCommentSending ? 'none' : 'auto' }}
                disabled={isCommentSending || !commentText.trim()}
                onClick={() => {
                  if (commentText.trim() && !isCommentSending) {
                    if (commentModal.congratId) handleComment(commentModal.congratId, commentText.trim(), false);
                    if (commentModal.newsId) handleComment(commentModal.newsId, commentText.trim(), true);
                  }
                }}
              >
                {isCommentSending ? 'Отправка...' : 'Отправить'}
              </Button>
            </div>
          </CommentModal>
        </CommentModalBg>
      )}
      
    </Wrapper>
  );
}